<template>
  <v-container fluid class="viewer-container d-flex flex-column" style="user-select: none;">
    <div v-if="selectedAsset != null" ref="viewerStage" class="viewer-stage flex-grow-1">
      <!-- Full-bleed model viewer -->
      <div v-if="modelVersion != null" class="viewer-wrap">
        <ModelViewer class="full-size-viewer" ref="modelViewer" :assetId=selectedAsset.id :modelVersion="modelVersion"
          :markers="markers" @onModelLoaded="onModelLoaded" @markerSelected="onMarkerSelected"
          @markerModified="onMarkerModified" @insideInspectionArea="onNavigatedToInspectionArea"
          @onPointSelected="onPointSelected" @onAreaConflict="displayAreaConflictDialog"
          @isTransformActive="onTransformActiveChanged"
          @mobileMarkerSelected="onMobileMarkerSelected"
          @mobileHotSpotSelected="onMobileHotSpotSelected"
          @onPlaybackState="onPlaybackState" @onBubbleState="onBubbleState"
          :showPegman="false" :showBubbleBar="!isDesktopPointer" :hoverPlacement="isDesktopPointer"
          :allowAnnotate="can(Permissions.DualloAdminView) && can(Permissions.AnomalyCreate) && isDesktopPointer"
          @onBubbleAnnotation="onBubbleAnnotation" @onBubbleAnnotationError="onBubbleAnnotationError"
          @onBubbleReimage="onBubbleReimage" @onStreetViewAvailable="onStreetViewAvailable" />
      </div>

      <EditMarkerToolBar :isVisible="isTransformActive" :isSaving="isTransformSaving" @onRotate="onRotateTransformSelected"
        @onScale="onScaleTransformSelected" @OnMove="onMoveTransformSelected" @onCancel="onTransformCancelled"
        @onSave="onTransformSaveClicked" />

      <!-- Baking banner: the optimised PLYs are still being generated, so the viewer is loading the
           full-resolution version. Dismissible, but reappears on reload while the bake is in flight. -->
      <div v-if="modelVersion && modelVersion.isBaking && !bakingBannerDismissed" class="baking-banner">
        <v-icon size="20" color="#ffd166" class="baking-banner-icon">mdi-progress-clock</v-icon>
        <span class="baking-banner-text">
          Optimised model is still processing — showing the full-resolution version. Performance may be reduced.
        </span>
        <button type="button" class="baking-banner-close" title="Dismiss"
          @click="bakingBannerDismissed = true">
          <v-icon size="18">mdi-close</v-icon>
        </button>
      </div>


      <!-- ═══════════ Mobile viewer chrome (hidden in street view, which shows its own Exit) ═══════════ -->
      <template v-if="useMobileUx && !bubbleActive">
        <!-- Top bar -->
        <div class="mv-topbar">
          <button type="button" class="mv-round" aria-label="Back" @click="goBack"><v-icon size="22">mdi-chevron-left</v-icon></button>
          <div class="mv-title">
            <span class="mv-title-main">{{ selectedAsset.name }}</span>
            <span class="mv-title-sub">{{ mobileSubline }}</span>
          </div>
        </div>

        <!-- Right rail (bottom-right, thumb range): Layers on top, Street view on the bottom.
             Hidden while the markers list is expanded or a marker's detail sheet is open. -->
        <div v-if="!sheetExpanded && !mobileDetailPanel" class="mv-rail">
          <button type="button" class="mv-rail-btn" :class="{ active: mobileLayersOpen }" title="Layers"
            @click="toggleMobilePanel('layers')">
            <v-icon size="22">mdi-layers-outline</v-icon>
          </button>
          <button type="button" class="mv-rail-btn" :class="{ active: mobileWalkOpen }" title="Walkthrough playback"
            @click="toggleMobilePanel('walk')">
            <v-icon size="22">mdi-play-circle-outline</v-icon>
          </button>
          <button type="button" class="mv-rail-btn peg-btn" :class="{ 'is-disabled': !streetViewAvailable }"
            :disabled="!streetViewAvailable" title="Street view — drag onto a walking path"
            @pointerdown="onStreetViewPointerDown">
            <!-- eslint-disable-next-line vue/no-v-html -->
            <svg class="peg-fig" viewBox="0 0 44 68" width="22" height="34" v-html="pegmanSvg"></svg>
          </button>
        </div>
        <transition name="mv-drop">
          <div v-if="mobileLayersOpen && !sheetExpanded && !mobileDetailPanel" class="mv-layers"><MobileLayerToggles /></div>
        </transition>
        <transition name="mv-drop">
          <div v-if="mobileWalkOpen && !sheetExpanded && !mobileDetailPanel" class="mv-walk">
            <ModelToolsCard only="walk" :bounds="modelBounds" :playback-state="playbackState"
              @toggle="onPlaybackToggle" @stop="onPlaybackStop" @seek="onPlaybackSeek" />
          </div>
        </transition>

        <!-- Bottom sheet -->
        <div class="mv-sheet" :style="mvSheetStyle">
          <button type="button" class="mv-handle" aria-label="Resize sheet" @click="toggleSheet"
            @touchstart.passive="onHandleStart" @touchend="onHandleEnd">
            <span></span>
          </button>

          <MarkerDetailPanel v-if="mobileMarkerPanel" class="mv-detail" :marker="selectedMarker"
            :assetId="selectedAsset.id" :scanName="scanNames[selectedMarker.scanId]" :areaName="selectedAreaName"
            :frames="identifiedFrames" :index="markerIndex" :total="markerTotal" :compact="!detailFull" :mobile="true"
            :backLabel="markerBackLabel"
            @back="markerPanelBack"
            @toggleSize="detailFull = !detailFull"
            @locate="onNavigateToMarker"
            @onMarkerReviewed="markerReviewStatusChanged" @onAnomalyDeleted="deleteAnomalyMarker"
            @onThumbnailUpdated="markerThumbnailUpdated" :imageRefresh="markerImageRefresh" />

          <!-- Hotspot on mobile: same bottom-sheet pattern as a marker (full actions minus Move — no
               transform on touch). Its anomaly list opens each anomaly in the marker sheet, Back returns here. -->
          <HotSpotDetailPanel v-else-if="mobileHotSpotPanel" class="mv-detail" :hotSpot="selectedHotSpot"
            :areaName="hotSpotAreaName" :backLabel="hotSpotBackLabel" :imageRefresh="hotSpotImageRefresh"
            @back="hotSpotPanelBack"
            @onHotSpotDeleted="hotSpotDeleted" @onHotSpotThumbnailUpdated="hotSpotThumbnailUpdated"
            @navigateToMarker="onNavigateToMarkerFromHotSpot" />

          <!-- Inspection zone on mobile: metrics / isolate / member lists (same panel as desktop). -->
          <JITSidebar v-else-if="mobileZonePanel" class="mv-detail" :asset="selectedAsset"
            :inspectionArea="navigatedInspectionArea" :showBack="true"
            @back="zonePanelBack" @navigateToMarker="onNavigateToMarker" @navigateToHotSpot="navigateToHotSpot"
            @displayInspectionAreaDetails="onDisplayInspectionAreaDetails" @toggleIsolate="onToggleZoneIsolation" />

          <template v-else>
            <button type="button" class="mv-sheet-head" @click="sheetExpanded = !sheetExpanded">
              <span class="mv-sheet-title">Markers</span>
              <span class="mv-sheet-meta">{{ markerTotal }} · <span class="amber">{{ mobilePending }} pending</span></span>
              <span class="mv-sheet-all">{{ sheetExpanded ? 'Collapse' : 'See all' }}</span>
            </button>
            <MarkerGroupSearchComponent v-if="sheetExpanded" class="mv-list" :asset="selectedAsset" :markers="markers"
              :modelLoaded="modelLoaded" :scanNames="scanNames" :selectedId="selectedMarker?.id"
              @navigateToMarker="onNavigateToMarker" />
          </template>
        </div>
      </template>

      <!-- Breadcrumb pill (desktop) — its own floating element, top-left. -->
      <div class="viewer-crumb" :class="(useMobileUx || bubbleActive) ? 'd-none' : 'd-flex'">
        <button type="button" class="tb-back" title="Back to assets" @click="goBack">
          <v-icon size="18">mdi-arrow-left</v-icon>
        </button>
        <nav class="tb-crumbs" aria-label="Breadcrumb">
          <button type="button" class="tb-crumb" @click="goBack">Assets</button>
          <span class="tb-sep">/</span>
          <span class="tb-crumb current" :title="selectedAsset.name">{{ selectedAsset.name }}</span>
          <template v-if="versionLabel">
            <span class="tb-sep">/</span>
            <span class="tb-version">{{ versionLabel }}</span>
          </template>
        </nav>
      </div>

      <!-- Tools pill (desktop) — its own floating element, centred over the canvas. -->
      <div class="viewer-tools" :class="(useMobileUx || bubbleActive) ? 'd-none' : 'd-flex'">
        <button type="button" class="tb-mode peg-btn" :class="{ 'is-disabled': !streetViewAvailable }"
          :disabled="!streetViewAvailable" title="Street view — drag onto a walking path"
          @pointerdown="onStreetViewPointerDown">
          <!-- eslint-disable-next-line vue/no-v-html -->
          <svg class="peg-fig" viewBox="0 0 44 68" width="16" height="25" v-html="pegmanSvg"></svg>
          <span>Street view</span>
        </button>
        <button type="button" class="tb-mode" :class="{ active: activeTool === 'walk' }"
          title="Walkthrough playback" @click="selectTool('walk')">
          <v-icon size="18">mdi-play-circle-outline</v-icon>
          <span>Walkthrough</span>
        </button>

        <span class="tb-div"></span>

        <button type="button" class="tb-icon" :class="{ active: activeTool === 'layers' }"
          title="Layers &amp; visibility" @click="selectTool('layers')">
          <v-icon size="20">mdi-layers-outline</v-icon>
        </button>
        <button type="button" class="tb-icon" :class="{ active: activeTool === 'slice' }"
          title="Slice / cut-away" @click="selectTool('slice')">
          <v-icon size="20">mdi-crop</v-icon>
        </button>
        <button type="button" class="tb-icon" :class="{ active: activeTool === 'help' }"
          title="Controls &amp; info" @click="selectTool('help')">
          <v-icon size="20">mdi-information-outline</v-icon>
        </button>
        <button type="button" class="tb-icon" :title="isFullscreen ? 'Exit fullscreen' : 'Fullscreen'"
          @click="toggleFullscreen">
          <v-icon size="20">{{ isFullscreen ? 'mdi-fullscreen-exit' : 'mdi-fullscreen' }}</v-icon>
        </button>
      </div>

      <!-- Street-view toolbar (bottom-centre). Parent-owned; the child (ModelViewer) does the drawing.
           Desktop-mouse only; touch devices use ModelViewer's own bar, so there's always exactly one.
           Shows the capture / "Create thumbnail" controls only in capture mode (re-capture or just-
           created marker/hotspot) — never while simply looking around. -->
      <div v-if="bubbleActive" class="viewer-bubble-exit" :class="isDesktopPointer ? 'd-flex' : 'd-none'">
        <span class="be-label"><v-icon size="16" color="#64ffda">mdi-image-filter-center-focus</v-icon><span class="be-text">Street view</span></span>

        <div v-if="bubble.captureCount > 1" class="be-steps">
          <button type="button" class="be-btn be-step" title="Previous capture"
            :disabled="bubble.captureIndex <= 0" @click="bubbleStep(-1)">
            <v-icon size="18">mdi-chevron-left</v-icon>
          </button>
          <v-menu location="top" :close-on-content-click="true">
            <template #activator="{ props }">
              <button type="button" class="be-btn be-counter" v-bind="props" title="Jump to a capture">
                <span>{{ bubble.captureIndex + 1 }} / {{ bubble.captureCount }}</span>
                <v-icon size="14">mdi-menu-down</v-icon>
              </button>
            </template>
            <v-list theme="dark" density="compact" class="be-jump-list">
              <v-list-item v-for="c in bubble.captures" :key="c.index" :active="c.index === bubble.captureIndex"
                @click="bubbleJump(c.index)">
                <v-list-item-title>{{ c.label }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
          <button type="button" class="be-btn be-step" title="Next capture"
            :disabled="bubble.captureIndex >= bubble.captureCount - 1" @click="bubbleStep(1)">
            <v-icon size="18">mdi-chevron-right</v-icon>
          </button>
        </div>

        <template v-if="bubble.captureMode">
          <span class="be-sep"></span>
          <button v-if="!bubble.drawActive" type="button" class="be-btn be-mark" @click="bubbleStartDraw">
            <v-icon size="16">mdi-vector-polygon</v-icon><span>Create thumbnail</span>
          </button>
          <template v-else>
            <span class="be-hint">{{ bubble.drawPoints < 3 ? 'Outline the finding' : 'Drag a point to adjust, or add more' }}</span>
            <button type="button" class="be-btn be-create" :disabled="bubble.drawPoints < 3" @click="bubbleSaveDraw">
              <v-icon size="16">mdi-check</v-icon><span>Save</span>
            </button>
            <button type="button" class="be-btn" :disabled="!bubble.drawPoints" @click="bubbleClearDraw">
              <v-icon size="16">mdi-backup-restore</v-icon><span>Clear</span>
            </button>
            <button type="button" class="be-btn" @click="bubbleCancelDraw">
              <v-icon size="16">mdi-close</v-icon><span>Cancel</span>
            </button>
          </template>
          <span class="be-sep"></span>
        </template>

        <button type="button" class="be-btn" @click="exitBubble">
          <v-icon size="16">mdi-close</v-icon><span>Exit</span>
        </button>
      </div>

      <!-- Tool panel (desktop): the active tool's card floats directly under the tools pill. -->
      <aside v-if="activeTool" class="viewer-tool-panel" :class="(useMobileUx || bubbleActive) ? 'd-none' : 'd-flex'">
        <div class="rail-panel">
          <LayerVisibilityCard v-if="activeTool === 'layers'" :forceOpen="true" />

          <ModelNavigationToolsCard v-else-if="activeTool === 'help'" :forceOpen="true" />

          <ModelToolsCard v-else-if="activeTool === 'slice'" only="slice"
            :bounds="modelBounds" :playback-state="playbackState"
            @apply="onSliceApply" @clear="onSliceClear" />

          <ModelToolsCard v-else-if="activeTool === 'walk'" only="walk"
            :bounds="modelBounds" :playback-state="playbackState"
            @toggle="onPlaybackToggle" @stop="onPlaybackStop" @seek="onPlaybackSeek"
            @speed="onPlaybackSpeed" @selectScan="onPlaybackSelectScan" />
        </div>
      </aside>

      <!-- Right panel (desktop): the markers list, or the selected marker's detail. -->
      <aside class="viewer-right" :class="(useMobileUx || bubbleActive) ? 'd-none' : 'd-flex'">
        <MarkerDetailPanel v-if="showMarkerPanel" :marker="selectedMarker" :assetId="selectedAsset.id"
          :scanName="scanNames[selectedMarker.scanId]" :areaName="selectedAreaName" :frames="identifiedFrames"
          :index="markerIndex" :total="markerTotal" :allowTransform="isDesktopPointer"
          :backLabel="markerBackLabel"
          @back="markerPanelBack"
          @locate="onNavigateToMarker" @transformMarker="onTransformMarker"
          @onMarkerReviewed="markerReviewStatusChanged" @onAnomalyDeleted="deleteAnomalyMarker"
          @onThumbnailUpdated="markerThumbnailUpdated" @reimageInStreetView="onReimageRequested"
          :imageRefresh="markerImageRefresh" />

        <HotSpotDetailPanel v-else-if="showHotSpotPanel" :hotSpot="selectedHotSpot" :areaName="hotSpotAreaName"
          :allowTransform="isDesktopPointer" :backLabel="hotSpotBackLabel" :imageRefresh="hotSpotImageRefresh"
          @back="hotSpotPanelBack" @transformHotSpot="onTransformMarker"
          @onHotSpotDeleted="hotSpotDeleted" @onHotSpotThumbnailUpdated="hotSpotThumbnailUpdated"
          @reimageInStreetView="onReimageRequested"
          @navigateToMarker="onNavigateToMarkerFromHotSpot" />

        <JITSidebar v-else-if="showZonePanel" :asset="selectedAsset" :inspectionArea="navigatedInspectionArea"
          :showBack="true" @back="zonePanelBack" @navigateToMarker="onNavigateToMarker"
          @navigateToHotSpot="navigateToHotSpot" @displayInspectionAreaDetails="onDisplayInspectionAreaDetails"
          @toggleIsolate="onToggleZoneIsolation" />

        <div v-else class="markers-panel">
          <AnomalyToolBar v-if="can(Permissions.AnomalyCreate) && isDesktopPointer" :selectedPoint="selectedPoint"
            @createMarker="onCreateMarkerRequested" @createInspectionArea="onCreateInspectionAreaRequested"
            @onCreateHotSpot="createHotSpot" />

          <MarkerGroupSearchComponent :asset="selectedAsset" :markers="markers" :modelLoaded="modelLoaded"
            :scanNames="scanNames" :selectedId="selectedMarker?.id" @navigateToMarker="onNavigateToMarker" />
        </div>
      </aside>
    </div>

    <OverlayWindow :isVisible="isInspectionAreaOverlayVisible" @closeOverlay="isInspectionAreaOverlayVisible = false"
      :centralizeWindow="true" overlayTitle="Inspection Area Details">
      <InspectionAreaDetails :area="inspectionAreaBeingEdited" :allowTransform="isDesktopPointer"
        @transformInspectionArea="onTransformMarker"
        @renameInspectionArea="onRenameInspectionArea" @deleteInspectionArea="onDeleteInspectionArea" />
    </OverlayWindow>
  </v-container>

  <SimpleDialogMessage ref="dialogMessage" :title="dialogTitle" :message="dialogMessage" />

  <!-- Thumbnail save: a loader that appears the moment street view exits, then flips to success/error. -->
  <v-dialog v-model="saveDialog.open" max-width="340" :persistent="saveDialog.phase === 'saving'">
    <div class="save-modal">
      <div class="save-icon" :class="saveDialog.phase">
        <v-progress-circular v-if="saveDialog.phase === 'saving'" indeterminate size="30" width="3" color="#64ffda" />
        <v-icon v-else-if="saveDialog.phase === 'done'" size="30" color="#4ade80">mdi-check</v-icon>
        <v-icon v-else size="30" color="#f87171">mdi-alert-circle-outline</v-icon>
      </div>
      <h2 class="save-title">
        {{ saveDialog.phase === 'saving' ? 'Saving image…' : saveDialog.phase === 'done' ? 'Thumbnail updated' : 'Couldn’t save image' }}
      </h2>
      <p v-if="saveDialog.phase !== 'done' && (saveDialog.phase === 'saving' || saveDialog.message)" class="save-text">
        {{ saveDialog.phase === 'saving' ? 'Uploading the new thumbnail…' : saveDialog.message }}
      </p>
      <v-progress-linear v-if="saveDialog.phase === 'saving'" indeterminate rounded height="4" color="#64ffda" class="save-bar" />
      <button v-if="saveDialog.phase === 'error'" type="button" class="save-close" @click="saveDialog.open = false">Close</button>
    </div>
  </v-dialog>

  <!-- Street-view annotation: choose the marker type for the polygon just drawn, then create. -->
  <v-dialog v-model="bubbleTypeDialog" max-width="400px" @update:model-value="v => { if (!v) cancelBubbleType(); }">
    <v-card class="bubble-type-card">
      <v-card-title class="bubble-type-title">Create anomaly from area</v-card-title>
      <v-card-text>
        <v-select v-model="bubbleSelectedType" :items="bubbleMarkerTypes" item-title="label" item-value="id"
          return-object label="Select marker type" variant="outlined" density="comfortable" hide-details="auto" />
      </v-card-text>
      <v-card-actions class="px-4 pb-4">
        <v-spacer />
        <v-btn class="bt-ghost" @click="cancelBubbleType">Cancel</v-btn>
        <v-btn class="bt-primary" :disabled="!bubbleSelectedType" @click="confirmBubbleType">Create</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <MobileViewerHint v-model="showMobileHint" @acknowledge="dismissMobileHint" />
</template>

<script>
import MarkerDetailPanel from '@/components/MarkerDetailPanel.vue';
import HotSpotDetailPanel from '@/components/HotSpotDetailPanel.vue';
import ModelViewer from '@/components/ModelViewer.vue';
import OverlayWindow from '@/components/OverlayWindow.vue';
import AnomalyToolBar from '@/components/AnomalyToolBar.vue';
import MarkerGroupSearchComponent from '@/components/MarkerGroupSearchComponent.vue';
import EditMarkerToolBar from '@/components/EditMarkerToolBar.vue';
import JITSidebar from '@/components/JITSidebar.vue';
import CreateMarkerModel from '@/domain/marker/CreateMarkerModel';
import UpdateMarkerThumbnailModel from '@/domain/marker/UpdateMarkerThumbnailModel';
import { getBase64AndExtension } from '@/utils/FileManipulation';
import MarkerService from '@/services/MarkerService.js';
import MarkerProvenanceService from '@/services/MarkerProvenanceService';
import HotSpotService from '@/services/HotSpotService';
import AnomalyMarker from '@/domain/marker/AnomalyMarker';
import HotSpotMarker from '@/domain/marker/HotSpotMarker';
import ModelVersionService from '@/services/ModelVersionService';
import { useAssetStore } from '@/store/assetStore';
import ModelNavigationToolsCard from '@/components/cards/ModelNavigationToolsCard.vue';
import ModelToolsCard from '@/components/cards/ModelToolsCard.vue';
import InspectionAreaService from '@/services/InspectionAreaService';
import SimpleDialogMessage from '@/components/dialog/SimpleDialogMessage.vue';
import CreateInspectionAreaModel from '@/domain/marker/CreateInspectionAreaModel';
import InspectionAreaDetails from '@/components/InspectionAreaDetails.vue';
import InspectionAreaMarker from '@/domain/marker/InspectionAreaMarker';
import UpdateInspectionAreaModel from '@/domain/marker/UpdateInspectionAreaModel';
import CreateHotSpotModel from '@/domain/marker/CreateHotSpotModel';
import MobileViewerHint from '@/components/windows/MobileViewerHint.vue';
import MobileLayerToggles from '@/components/MobileLayerToggles.vue';
import LayerVisibilityCard from '@/components/LayerVisibilityCard.vue';
import organizationSasService from '@/services/OrganizationSasService';
import { useDisplay } from 'vuetify';
import { useDeviceCapability } from '@/composable/useDeviceCapability';
import { Permissions } from '@/constants/permissions';
import { usePermissions } from '@/composable/usePermissions';
import { useViewerUiStore } from '@/store/viewerUiStore';
import { useMarkerStore } from '@/store/markerStore';
import { mapState } from 'pinia';
import * as markerGeometry from '@/lib/markerGeometry';
import { setMarkerStyles } from '@/lib/markerStyleRegistry';

export default {
  name: 'ViewAsset',
  components: {
    ModelViewer,
    MarkerDetailPanel,
    HotSpotDetailPanel,
    MarkerGroupSearchComponent,
    OverlayWindow,
    AnomalyToolBar,
    EditMarkerToolBar,
    JITSidebar,
    ModelNavigationToolsCard,
    ModelToolsCard,
    SimpleDialogMessage,
    InspectionAreaDetails,
    MobileViewerHint,
    MobileLayerToggles,
    LayerVisibilityCard
  },
  setup() {
    const { can } = usePermissions();
    const { mdAndUp } = useDisplay();
    // Creating markers/zones/hotspots needs a precise pointer (click a point, drag the gizmo), so it
    // is desktop-mouse only — hidden on touch phones/tablets where it's too fiddly.
    const { isDesktopPointer } = useDeviceCapability();

    return { can, Permissions, mdAndUp, isDesktopPointer, viewerUi: useViewerUiStore(), markerStore: useMarkerStore() }
  },
  computed: {
    // Marker data lives in markerStore; mapState gives a guaranteed-reactive `markers`
    // computed (a manual `this.markerStore.markers` getter didn't reliably re-render the
    // template/prop when the store array changed). All existing reads keep working.
    ...mapState(useMarkerStore, ['markers']),

    // Mobile UX below the app-wide 960px line (matches AppLayout / HomePage / Profile etc.), so a
    // wide tablet (e.g. iPad Pro portrait at 1024px) gets the desktop layout consistently and does
    // not mix mobile chrome with desktop chrome. Drives layout + touch popup/hint behaviour.
    useMobileUx() {
      return !this.mdAndUp;
    },
    // Desktop: when an anomaly marker is selected, the right column shows its detail panel (replacing
    // the markers list). Hotspots / inspection zones keep their own windows.
    showMarkerPanel() {
      return !this.useMobileUx && !!this.selectedMarker && this.selectedMarker instanceof AnomalyMarker;
    },
    // The set the detail panel's prev/next steps through (and the "X of N" count).
    navMarkers() {
      return this.markers.filter(m => m instanceof AnomalyMarker);
    },
    markerTotal() {
      return this.navMarkers.length;
    },
    markerIndex() {
      if (!this.selectedMarker) return -1;
      return this.navMarkers.findIndex(m => m.id === this.selectedMarker.id);
    },
    selectedAreaName() {
      const id = this.selectedMarker?.inspectionAreaId;
      if (!id) return '';
      const area = this.markers.find(m => m.id === id && m instanceof InspectionAreaMarker);
      return area?.name || '';
    },
    // Mobile: the bottom-sheet shows a marker's detail when an anomaly is selected.
    mobileMarkerPanel() {
      return this.useMobileUx && !!this.selectedMarker && this.selectedMarker instanceof AnomalyMarker;
    },
    // Mobile: the bottom-sheet shows a hotspot's detail (same pattern as a marker) when a hotspot
    // is selected and no anomaly is open on top of it.
    mobileHotSpotPanel() {
      return this.useMobileUx && !!this.selectedHotSpot && !this.mobileMarkerPanel;
    },
    // Mobile: the bottom-sheet shows an inspection zone's panel (metrics / isolate / member lists)
    // when the viewer is inside a zone and no marker/hotspot detail is open on top of it.
    mobileZonePanel() {
      return this.useMobileUx && !!this.navigatedInspectionArea
        && !this.mobileMarkerPanel && !this.mobileHotSpotPanel;
    },
    // Any detail panel is filling the sheet — the markers list and rail step aside for it.
    mobileDetailPanel() {
      return this.mobileMarkerPanel || this.mobileHotSpotPanel || this.mobileZonePanel;
    },
    // Back-button label reflects where a marker was opened from — its immediate return target:
    // a hotspot, an inspection zone, or the markers list.
    markerBackLabel() {
      if (this.selectedHotSpot) return 'Hotspot';
      if (this.navigatedInspectionArea) return 'Inspection zone';
      return 'Markers';
    },
    // A hotspot returns to the zone it was opened from, otherwise the markers list.
    hotSpotBackLabel() {
      return this.navigatedInspectionArea ? 'Inspection zone' : 'Markers';
    },
    mobilePending() {
      return this.navMarkers.filter(m => (m.status || '').toLowerCase() === 'pending').length;
    },
    mobileSubline() {
      const parts = [];
      if (this.modelVersion?.versionNumber != null) parts.push(`V${this.modelVersion.versionNumber}`);
      parts.push(`${this.markerTotal} markers`);
      return parts.join(' · ');
    },
    // Bottom-sheet height: a selected marker opens at half (keeps the model + its pin in view),
    // expandable to full; the markers list has its own peek/expanded heights.
    mvSheetStyle() {
      // Dynamic viewport units (dvh) so the sheet tracks the visible area as mobile browser chrome
      // shows/hides. Every state returns an explicit height (incl. peek) so height transitions animate.
      if (this.mobileDetailPanel) return { height: this.detailFull ? '88dvh' : '50dvh', maxHeight: '90dvh' };
      if (this.sheetExpanded) return { height: '82dvh', maxHeight: '90dvh' };
      return { height: 'calc(112px + env(safe-area-inset-bottom, 0px))', maxHeight: '90dvh' };
    },
    // Right column shows the hotspot panel when a hotspot is selected (desktop).
    showHotSpotPanel() {
      return !this.useMobileUx && !!this.selectedHotSpot && !this.showMarkerPanel;
    },
    hotSpotAreaName() {
      const id = this.selectedHotSpot?.inspectionAreaId;
      if (!id) return '';
      const area = this.markers.find(m => m.id === id && m instanceof InspectionAreaMarker);
      return area?.name || '';
    },
    // Noun for the re-capture banner — a hotspot or a marker.
    reimagingLabel() {
      return this.reimagingMarker instanceof HotSpotMarker ? 'hotspot' : 'marker';
    },
    // Right column shows the inspection-zone panel when a zone is selected and nothing else is.
    showZonePanel() {
      return !this.useMobileUx && !!this.navigatedInspectionArea && !this.showMarkerPanel && !this.showHotSpotPanel;
    },
    // "V1 · 12 Mar 2026" label for the breadcrumb (read-only; version switching isn't in the viewer).
    versionLabel() {
      const v = this.modelVersion;
      if (!v) return '';
      const num = v.versionNumber != null ? `V${v.versionNumber}` : (v.versionName || '');
      const dateSrc = v.createdOn || v.publishedOn || null;
      let datePart = '';
      if (dateSrc) {
        const d = new Date(dateSrc);
        if (!Number.isNaN(d.getTime())) {
          datePart = d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
        }
      }
      return datePart ? `${num} · ${datePart}` : num;
    },
  },
  data() {
    return {
      isInspectionAreaOverlayVisible: false,
      transformingInspectionArea: false,
      markerBeingTransformed: null,

      inspectionAreaBeingEdited: null,

      selectedAsset: null,
      modelVersion: null,
      // Local-only dismissal of the "still baking" banner; reset on every mount so it reappears
      // on reload while the version is still baking.
      bakingBannerDismissed: false,

      // scanId -> scan name, used to label markers with their associated scan
      scanNames: {},


      modelLoaded: false,
      modelBounds: null,   // world-space model extent from ModelViewer, feeds the slice tool
      playbackState: null, // walkthrough playback snapshot from ModelViewer, feeds the playback tool

      selectedPoint: null,
      selectedPointLocal: null,   // clicked point in the hit scan's local space (for storage)
      selectedScanId: null,       // the scan the clicked point is on
      selectedPointInspectionAreaId: null,
      selectedMarker: null,
      selectedHotSpot: null,
      // When navigating to a marker from a hotspot panel, remember it so the marker's Back returns
      // to the hotspot instead of the list. One-shot: cleared once the selection is applied.
      navReturnContext: null,

      // Street-view polygon annotation: the pending crop + type-picker state.
      pendingAnnotationImage: null,   // webp data URL captured from the bubble at Create time
      pendingProvenance: null,        // cube-face + bbox for the manual marker's provenance record
      reimagingMarker: null,          // marker being re-captured (image only) via street view, or null
      markerImageRefresh: 0,          // bumped after a marker's image changes so the detail panel re-signs it
      hotSpotImageRefresh: 0,         // same, for the hotspot detail panel after a re-capture
      bubbleTypeDialog: false,
      bubbleMarkerTypes: [],
      bubbleSelectedType: null,

      isTransformActive: false,
      isTransformSaving: false,

      showMobileHint: false,

      navigatedInspectionArea: null,
      // visibility moved to viewerUiStore (see this.viewerUi.layers)

      frames: [],
      identifiedFrames: [],

      assetStore: null,
      showMessageDialog: false,
      dialogTitle: "",
      dialogMessage: "",
      // Thumbnail-save dialog: opens as a 'saving' loader the moment street view exits, then flips to
      // 'done' (success) or 'error' when the upload finishes.
      saveDialog: { open: false, phase: 'saving', message: '' },


      leftCollapsed: false,
      rightCollapsed: false,

      // Viewer tool rail: which tool panel is open ('layers' | 'help' | 'slice' | 'walk' | null),
      // and whether the model has capture points so the Street-view (pegman) control is usable.
      activeTool: null,
      streetViewAvailable: false,
      isFullscreen: false,
      bubbleActive: false,
      // Full bubble state from the child (ModelViewer), driving the parent-owned street-view toolbar.
      bubble: { active: false, captureMode: false, drawActive: false, captureCount: 0, captureIndex: -1, drawPoints: 0, captures: [], captureLabel: '' },
      // Mobile viewer chrome
      sheetExpanded: false,
      mobileLayersOpen: false,
      mobileWalkOpen: false,   // mobile rail: walkthrough playback panel
      detailFull: false,   // mobile marker sheet: false = half (image+status), true = full detail
      sheetDragStartY: null,

      // Google-Maps-style pegman for the rail's Street-view handle (mirrors ModelViewer's figure).
      pegmanSvg: `
        <ellipse cx="22" cy="16.6" rx="12.6" ry="3.1" fill="#e9a417"/>
        <path d="M13.2 16.6 a8.8 8.8 0 0 1 17.6 0 z" fill="#ffc62e"/>
        <path d="M15 16.6 a7 7 0 0 1 7 -6.8 l0 6.8 z" fill="#ffdc6b"/>
        <rect x="21" y="9.6" width="2" height="7" rx="1" fill="#e9a417"/>
        <circle cx="22" cy="22.4" r="5.2" fill="#eab98d"/>
        <path d="M17 23.2 a5.2 5.2 0 0 0 10 0 z" fill="#d9a578" opacity="0.5"/>
        <rect x="19.5" y="26" width="5" height="3" rx="1.5" fill="#d9a578"/>
        <rect x="13.6" y="27.6" width="16.8" height="21" rx="6.5" fill="#2c313c"/>
        <rect x="12.9" y="28.5" width="18.2" height="19.4" rx="6.2" fill="#ddf23a"/>
        <path d="M19 28.7 l3 3 l3 -3 z" fill="#2c313c"/>
        <rect x="21.6" y="31" width="0.8" height="16.6" fill="#aeb90f"/>
        <rect x="11.8" y="34.4" width="20.4" height="2.6" rx="1.1" fill="#d7e1e8"/>
        <rect x="11.8" y="40.2" width="20.4" height="2.6" rx="1.1" fill="#d7e1e8"/>
        <rect x="8.7" y="29" width="5" height="15.2" rx="2.5" fill="#2c313c"/>
        <rect x="30.3" y="29" width="5" height="15.2" rx="2.5" fill="#2c313c"/>
        <rect x="8.7" y="40.6" width="5" height="3.1" rx="1.4" fill="#ddf23a"/>
        <rect x="30.3" y="40.6" width="5" height="3.1" rx="1.4" fill="#ddf23a"/>
        <circle cx="11.2" cy="45.4" r="2.5" fill="#23272f"/>
        <circle cx="32.8" cy="45.4" r="2.5" fill="#23272f"/>
        <rect x="15.4" y="47.6" width="5.6" height="15.6" rx="2.3" fill="#3a4150"/>
        <rect x="23" y="47.6" width="5.6" height="15.6" rx="2.3" fill="#3a4150"/>
        <rect x="13.4" y="60" width="8" height="4.7" rx="2.3" fill="#17191f"/>
        <rect x="22.6" y="60" width="8" height="4.7" rx="2.3" fill="#17191f"/>
        <rect x="13.4" y="63.2" width="8" height="1.5" rx="0.7" fill="#454b55"/>
        <rect x="22.6" y="63.2" width="8" height="1.5" rx="0.7" fill="#454b55"/>
      `,
    };
  },
  watch: {
    // A newly selected inspection zone takes over the right column (its panel), so clear any marker /
    // hotspot selection. Refreshes of the SAME zone (after create/delete) keep the current selection.
    navigatedInspectionArea(area, previous) {
      if (area && area.id !== previous?.id) {
        this.selectedMarker = null;
        this.selectedHotSpot = null;
      }
    },
    // Each newly selected marker opens the mobile sheet at the half height.
    selectedMarker(marker) {
      if (marker) this.detailFull = false;
    },
  },
  async mounted() {
    document.addEventListener('fullscreenchange', this.onFullscreenChange);

    // First-time touch primer on phones/tablets (one-shot, persisted in localStorage).
    this.maybeShowMobileHint();

    // markerStore is a singleton that persists across route changes; reset it on entry
    // so re-opening an asset doesn't accumulate stale/duplicate markers.
    this.markerStore.clear();
    MarkerProvenanceService.invalidate();   // drop any previous asset's cached provenance

    this.getAsset();

    // The asset comes from a singleton store that's empty on a hard refresh / deep-link. Without
    // it every load below dereferences a null id and the page throws — bounce back to the list.
    if (!this.selectedAsset?.id) {
      this.$router.replace({ name: 'Assets' });
      return;
    }

    try {
      await this.getCurrentModelVersion();

      // Populate the marker-style registry (category → shape/colour) BEFORE loading markers,
      // since AnomalyMarker resolves its geometry/colour from it at construction time.
      await this.loadMarkerTypes();

      // allSettled (not all): one failing load shouldn't blank out the rest of the viewer.
      await Promise.allSettled([
        this.loadCameraHelpers(),
        this.loadAnomalyMarkers(),
        this.loadHotSpotMarkers(),
        this.loadInspectionAreaMarkers(),
        this.loadFrames(),
        this.loadScanNames(),
        this.loadProvenance(),
      ]);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to initialise the asset viewer', error);
      }
    }
  },
  beforeUnmount() {
    document.removeEventListener('fullscreenchange', this.onFullscreenChange);
  },
  methods: {
    removeMarkerFromArray(marker) {
      this.markerStore.remove(marker.id);
    },
    addMarkerToArray(marker) {
      this.markerStore.add(marker);
    },
    updateMarkerInArray(updatedMarker) {
      this.markerStore.update(updatedMarker);
    },
    async deleteAnomalyMarker(marker) {
      // delete the marker
      var deleted = await MarkerService.deleteMarker(marker);

      if (deleted) {
        this.removeMarkerFromArray(marker);

        if (marker.inspectionAreaId != null && marker.inspectionAreaId != "") {
          const updatedZone = await InspectionAreaService.getInspectionZoneByScanId(marker.scanId, marker.inspectionAreaId);
          this.onNavigatedToInspectionArea(updatedZone);
        }

        this.selectedMarker = null;   // side panel closes → back to the markers list

        this.dialogTitle = "Anomaly Deleted";
        this.dialogMessage = "Anomaly was successfully deleted.";
        this.$refs.dialogMessage.open();
      }
    },
    getAsset() {
      this.initializeStore();
      this.selectedAsset = this.assetStore.asset;
    },
    onModelLoaded(bounds) {
      this.modelLoaded = true;
      this.modelBounds = bounds ?? null;
    },
    // Back to the previous route (asset list) — used by the compact breadcrumb.
    goBack() {
      this.$router.back();
    },
    // ----- Viewer tool rail -----
    // Toggle a tool panel open/closed (clicking the active tool's icon collapses it).
    selectTool(tool) {
      this.activeTool = this.activeTool === tool ? null : tool;
    },
    // Mobile rail: only one drop-panel (Layers / Walkthrough) open at a time.
    toggleMobilePanel(which) {
      if (which === 'layers') {
        this.mobileLayersOpen = !this.mobileLayersOpen;
        if (this.mobileLayersOpen) this.mobileWalkOpen = false;
      } else if (which === 'walk') {
        this.mobileWalkOpen = !this.mobileWalkOpen;
        if (this.mobileWalkOpen) this.mobileLayersOpen = false;
      }
    },
    // ModelViewer reports whether capture points exist → enable/disable the Street-view control.
    onStreetViewAvailable(available) {
      this.streetViewAvailable = !!available;
    },
    // ----- Marker detail panel (right column) -----
    markerPanelBack() {
      this.selectedMarker = null;
      this.$refs.modelViewer?.deselectMarkerGroup?.();
    },
    // Navigate to an anomaly from within the hotspot panel — keep the hotspot as the Back target.
    onNavigateToMarkerFromHotSpot(marker) {
      this.navReturnContext = 'hotspot';
      this.onNavigateToMarker(marker);
    },
    hotSpotPanelBack() {
      this.selectedHotSpot = null;
      this.selectedMarker = null;
      this.$refs.modelViewer?.deselectMarkerGroup?.();
    },
    zonePanelBack() {
      this.navigatedInspectionArea = null;
      this.selectedMarker = null;
      this.selectedHotSpot = null;
      this.$refs.modelViewer?.deselectMarkerGroup?.();
    },
    // Fullscreen the 3D stage (breadcrumb + panels come along since they live inside .viewer-stage).
    toggleFullscreen() {
      const el = this.$refs.viewerStage;
      if (!document.fullscreenElement) {
        el?.requestFullscreen?.();
      } else {
        document.exitFullscreen?.();
      }
    },
    onFullscreenChange() {
      this.isFullscreen = !!document.fullscreenElement;
    },
    // Start the pegman drag from the rail's Street-view button (same drag/drop as the built-in one).
    onStreetViewPointerDown(event) {
      if (!this.streetViewAvailable) return;
      this.$refs.modelViewer?.startPegDrag(event);
    },
    // ModelViewer reports entering/leaving the photo "bubble" (street view) → toggle the Exit control.
    onBubbleState(state) {
      // The child emits the full bubble state object; keep a boolean `bubbleActive` for the existing
      // layout bindings and a `bubble` object for the toolbar (capture mode, arrows, draw controls).
      const s = (state && typeof state === 'object') ? state : { active: !!state };
      this.bubble = {
        active: !!s.active, captureMode: !!s.captureMode, drawActive: !!s.drawActive,
        captureCount: s.captureCount || 0, captureIndex: s.captureIndex ?? -1, drawPoints: s.drawPoints || 0,
        captures: s.captures || [], captureLabel: s.captureLabel || '',
      };
      const active = this.bubble.active;
      this.bubbleActive = active;
      // Street view is immersive — hide every model layer on entry so only the photo shows, then
      // restore whatever the user had on when they exit.
      if (active) {
        if (!this._layersBeforeBubble) {
          this._layersBeforeBubble = { ...this.viewerUi.layers };
          const allOff = {};
          Object.keys(this.viewerUi.layers).forEach(k => { allOff[k] = false; });
          this.viewerUi.$patch({ layers: allOff });
        }
      } else if (this._layersBeforeBubble) {
        this.viewerUi.$patch({ layers: { ...this._layersBeforeBubble } });
        this._layersBeforeBubble = null;
      }
    },
    exitBubble() {
      this.$refs.modelViewer?.exitBubbleWithTransition?.();
    },
    // Street-view toolbar actions — the parent owns the bar, the child (ModelViewer) does the work.
    bubbleStep(dir) { this.$refs.modelViewer?.stepBubble?.(dir); },
    bubbleJump(index) { this.$refs.modelViewer?.jumpToCapture?.(index); },
    bubbleStartDraw() { this.$refs.modelViewer?.startBubbleDraw?.(); },
    bubbleSaveDraw() { this.$refs.modelViewer?.createFromBubblePolygon?.(); },
    bubbleClearDraw() { this.$refs.modelViewer?.clearBubbleDraw?.(); },
    bubbleCancelDraw() { this.$refs.modelViewer?.cancelBubbleDraw?.(); },
    // Slice tool (ModelToolsCard) → drive the viewer's world-space cut-away box.
    onSliceApply(box) {
      this.$refs.modelViewer?.setSliceBox(box);
    },
    onSliceClear() {
      this.$refs.modelViewer?.clearSliceBox();
    },
    // Walkthrough playback tool (ModelToolsCard) ↔ ModelViewer.
    onPlaybackState(state) {
      this.playbackState = state;
    },
    onPlaybackToggle() {
      this.$refs.modelViewer?.togglePlayback();
    },
    onPlaybackStop() {
      this.$refs.modelViewer?.stopPlayback();
    },
    onPlaybackSeek(fraction) {
      this.$refs.modelViewer?.seekPlayback(fraction);
    },
    onPlaybackSpeed(value) {
      this.$refs.modelViewer?.setPlaybackSpeed(value);
    },
    onPlaybackSelectScan(scanId) {
      this.$refs.modelViewer?.onSelectPlaybackScan(scanId);
    },
    initializeStore() {
      this.assetStore = useAssetStore();
    },
    async getCurrentModelVersion() {
      let versionResponse = await ModelVersionService.getCurrentModelVersion(this.selectedAsset.id);

      // No current model version (asset not scanned/processed yet) — nothing to load into the viewer.
      if (versionResponse == null) {
        this.modelVersion = null;
        return;
      }

      versionResponse.modelPath = await organizationSasService.getSignedUrl(versionResponse.modelPath);

      // Multi-scan versions: sign each scan's blob path too so the viewer can load them all.
      if (versionResponse.scans?.length) {
        versionResponse.scans = await Promise.all(versionResponse.scans.map(async (scan) => ({
          ...scan,
          modelPath: await organizationSasService.getSignedUrl(scan.modelPath),
        })));
      }

      this.modelVersion = versionResponse;
    },
    async loadFrames() {
      // Frames are scan-keyed; previously this only pulled the primary scan's frames so
      // anomalies on supplementary scans showed "No Images Available" even when their cameras
      // had captured them. Union across every scan in the current version.
      const framesByScan = await Promise.all(
        this.versionScanIds().map(id => ModelVersionService.getFramesByScanId(id)),
      );
      this.frames = [...this.frames, ...framesByScan.flat()];
    },
    async loadScanNames() {
      const scans = await ModelVersionService.getScansByAssetId(this.selectedAsset.id);
      this.scanNames = Object.fromEntries(scans.map(s => [s.id, s.name]));
    },
    async loadMarkerTypes() {
      // Resolve the org's categories into the style registry. On failure, fall back to legacy
      // defaults so markers still render (just without per-category shape/colour).
      try {
        const types = await MarkerService.getMarkerTypesByOrganizationId(this.selectedAsset.organizationId);
        setMarkerStyles(types);
      } catch {
        setMarkerStyles([]);
      }
    },
    async loadAnomalyMarkers() {
      // Anomalies are scan-keyed; load each scan in the version (store appends → unions).
      await Promise.all(this.versionScanIds().map(id => this.markerStore.loadAnomalyMarkers(id)));
    },
    async loadHotSpotMarkers() {
      await Promise.all(this.versionScanIds().map(id => this.markerStore.loadHotSpotMarkers(id)));
    },
    async loadInspectionAreaMarkers() {
      // Load each scan's inspection areas (they're scan-keyed); the store appends, so this unions.
      await Promise.all(this.versionScanIds().map(id => this.markerStore.loadInspectionAreaMarkers(id)));
    },
    async loadCameraHelpers() {
      await Promise.all(this.versionScanIds().map(id => this.markerStore.loadCameraHelpers(id)));
    },
    async loadProvenance() {
      // Warm the provenance cache once for every scan in the version, so selecting a finding reuses it
      // (to show the source image + detection box) instead of re-fetching the whole scan each time.
      await MarkerProvenanceService.preload(this.versionScanIds());
    },
    // Every scan in the current version (primary + supplements); falls back to the primary.
    versionScanIds() {
      const ids = (this.modelVersion.scans ?? []).map(s => s.scanId).filter(Boolean);
      return ids.length ? ids : [this.modelVersion.associatedScan].filter(Boolean);
    },
    onMarkerSelected(data) {
      // Keep the hotspot context when the marker was opened FROM the hotspot panel, so the marker's
      // Back button returns to that hotspot rather than all the way to the list.
      if (this.navReturnContext !== 'hotspot') this.selectedHotSpot = null;
      this.navReturnContext = null;

      this.selectedMarker = data.marker;

      // Only the marker's own scan's frames are relevant — a supplementary scan can re-use a
      // filename from the primary capture, and the user explicitly only wants images from the
      // scan the anomaly lives on.
      const filenameSet = new Set(data.imageFilenames);
      const markerScanId = this.selectedMarker?.scanId ?? null;
      this.identifiedFrames = this.frames.filter(frame =>
        filenameSet.has(frame.fileName) && (!markerScanId || frame.scanId === markerScanId),
      );


      if (this.selectedMarker instanceof HotSpotMarker) {
        this.selectedHotSpot = this.markers.find(m => m.id == this.selectedMarker.id)
      }

    },
    onMobileMarkerSelected(marker) {
      // Long-press on a marker (phone/tablet) → open it in the bottom-sheet detail (same as a tap).
      if (!this.useMobileUx) return;
      this.onNavigateToMarker(marker);
    },
    // Mobile bottom-sheet handle: toggle the marker detail half↔full, or the markers list peek↔expanded.
    toggleSheet() {
      if (this.mobileDetailPanel) this.detailFull = !this.detailFull;
      else this.sheetExpanded = !this.sheetExpanded;
    },
    onHandleStart(e) {
      this.sheetDragStartY = e.touches?.[0]?.clientY ?? null;
    },
    onHandleEnd(e) {
      if (this.sheetDragStartY == null) return;
      const endY = e.changedTouches?.[0]?.clientY ?? this.sheetDragStartY;
      const dy = endY - this.sheetDragStartY;
      this.sheetDragStartY = null;
      if (Math.abs(dy) < 40) return;   // a tap → let @click's toggle handle it
      e.preventDefault();              // it's a swipe; suppress the synthesized click
      const up = dy < 0;
      if (this.mobileDetailPanel) {
        // Detail sheet: swipe up → full; swipe down → half, then close back to what's underneath.
        if (up) this.detailFull = true;
        else if (this.detailFull) this.detailFull = false;
        else if (this.mobileMarkerPanel) this.markerPanelBack();
        else if (this.mobileHotSpotPanel) this.hotSpotPanelBack();
        else this.zonePanelBack();
      } else {
        // Markers list: swipe up → expanded, swipe down → peek.
        this.sheetExpanded = up;
      }
    },
    onMobileHotSpotSelected(hotSpot) {
      // Tap on a hotspot (mobile/tablet only) → hotspot detail in the bottom sheet (same pattern as
      // a marker). Its anomaly list opens each anomaly in the marker sheet on top.
      if (!this.useMobileUx) return;
      this.selectedMarker = null;
      this.selectedHotSpot = hotSpot;
      this.detailFull = false;
    },
    maybeShowMobileHint() {
      // Only on phones/tablets, and only until the user has acknowledged it once.
      if (!this.useMobileUx) return;
      try {
        if (localStorage.getItem('duallo.viewerTouchHintSeen')) return;
      } catch {
        // localStorage unavailable (private mode) — show it this session, just don't persist.
      }
      this.showMobileHint = true;
    },
    dismissMobileHint() {
      this.showMobileHint = false;
      try {
        localStorage.setItem('duallo.viewerTouchHintSeen', '1');
      } catch {
        // ignore persistence failures
      }
    },
    async onCreateMarkerRequested(markerType) {
      this.$refs.modelViewer?.exitBubbleForAction?.();
      const fromStreetView = !!this.pendingAnnotationImage;
      const markerRequest = this.createMarkerModelRequest(markerType);
      const marker = await MarkerService.CreateNewMarker(markerRequest);

      this.addMarkerToArray(marker);

      // Street-view flow: attach the polygon crop as the marker's thumbnail (best-effort).
      if (this.pendingAnnotationImage && marker?.id) {
        const img = this.pendingAnnotationImage;
        this.pendingAnnotationImage = null;
        await this.uploadAnnotationThumbnail(marker, img);
      }

      // Street-view flow: write the marker's provenance (cube face + bbox) so it carries the same
      // traceability as an AI detection — findings viewer, 360, and the training export (best-effort).
      if (this.pendingProvenance && marker?.id) {
        const prov = this.pendingProvenance;
        this.pendingProvenance = null;
        await this.writeAnnotationProvenance(marker, prov);
      }

      if (marker.inspectionAreaId != null && marker.inspectionAreaId != "") {
        const updatedZone = await InspectionAreaService.getInspectionZoneByScanId(marker.scanId, marker.inspectionAreaId);
        this.onNavigatedToInspectionArea(updatedZone);
      }

      // Street-view create: once the marker is synced into the viewer, select + zoom to it exactly
      // as a manual pick would (opens the detail panel, flies the camera in).
      if (fromStreetView && marker?.id) {
        await this.$nextTick();
        this.$refs.modelViewer?.locateMarkerById?.(marker.id);
      } else if (marker?.id) {
        // Toolbar create (picked a 3D point): drop into street view to capture its thumbnail.
        await this.enterCaptureForNew(marker);
      }
    },

    // ───────── Street-view polygon annotation ─────────
    // ModelViewer resolved a 3D point + captured a crop from the drawn polygon. Seed the create state
    // and open the type picker; the crop is uploaded as the thumbnail once the marker is created.
    async onBubbleAnnotation(data) {
      this.selectedPoint = data.point;
      this.selectedPointLocal = data.localPoint ?? data.point;
      this.selectedScanId = data.scanId ?? this.modelVersion.associatedScan;
      this.selectedPointInspectionAreaId = data.inspectionAreaId ?? null;
      this.pendingAnnotationImage = data.imageDataUrl ?? null;
      this.pendingProvenance = data.provenance ?? null;   // cube-face + bbox for the marker's provenance

      if (!this.bubbleMarkerTypes.length) {
        try { this.bubbleMarkerTypes = await MarkerService.getMarkerTypesByOrganizationId(this.selectedAsset.organizationId) ?? []; }
        catch { this.bubbleMarkerTypes = []; }
      }
      this.bubbleSelectedType = this.bubbleMarkerTypes[0] ?? null;
      this.bubbleTypeDialog = true;
    },
    onBubbleAnnotationError(message) {
      this.dialogTitle = 'Mark area';
      this.dialogMessage = message || 'Could not create a marker from that shape.';
      this.$refs.dialogMessage.open();
    },
    confirmBubbleType() {
      if (!this.bubbleSelectedType) return;
      this.bubbleTypeDialog = false;
      this.onCreateMarkerRequested(this.bubbleSelectedType);   // consumes pendingAnnotationImage
    },
    cancelBubbleType() {
      this.bubbleTypeDialog = false;
      this.pendingAnnotationImage = null;
      this.pendingProvenance = null;
      this.onPointDeselected();
    },
    // Write a manual marker's provenance (cube-face source + bbox the ModelViewer resolved), so it's
    // byte-compatible with an AI detection's record. Best-effort — never fails marker creation.
    // Returns true on success so callers that care (re-capture) can report failure; the create flow
    // ignores it (best-effort).
    async writeAnnotationProvenance(marker, provenance) {
      // No provenance to write (e.g. the detection fell on a cube face this capture is missing, so no
      // face image could be referenced). That's not a failure — the thumbnail still saves; this marker
      // just carries no face/bbox traceability. Returning true keeps the re-capture image from being
      // blocked on a provenance record we legitimately can't build.
      if (!provenance) return true;
      try {
        const pos = marker.transform?.position ?? {};
        await MarkerProvenanceService.create({
          scanId: marker.scanId,
          markerId: marker.id,
          label: marker.label || 'anomaly',
          mode: 'Manual',
          sourceImageFileName: provenance.sourceImageFileName,
          bboxX: provenance.bboxX,
          bboxY: provenance.bboxY,
          bboxWidth: provenance.bboxWidth,
          bboxHeight: provenance.bboxHeight,
          positionX: pos.x ?? 0,
          positionY: pos.y ?? 0,
          positionZ: pos.z ?? 0,
        });
        return true;
      } catch (e) {
        if (process.env.NODE_ENV === 'development') console.error('Failed to write manual marker provenance', e);
        return false;
      }
    },
    // Replace a marker's provenance: delete any existing record(s) for it (duallo-admin can delete),
    // then write the fresh one — so re-capture is idempotent, not a pile of stale duplicates.
    async replaceMarkerProvenance(marker, provenance) {
      try {
        const all = await MarkerProvenanceService.getByScanId(marker.scanId);
        const existing = (all || []).filter(r => r.markerId === marker.id);
        for (const r of existing) {
          await MarkerProvenanceService.deleteById(marker.scanId, r.id).catch(() => {});
        }
      } catch { /* non-fatal: a leftover old record is tolerable — getForMarker prefers the newest */ }
      return await this.writeAnnotationProvenance(marker, provenance);
    },
    // Re-capture: arm re-image for an EXISTING marker. The user then enters street view at a location
    // of their choosing and draws the box. Image-only.
    async onReimageRequested(marker) {
      const m = marker || this.selectedMarker;
      if (!m) return;
      const entered = await this.$refs.modelViewer?.startBubbleReimage?.(m);
      if (entered) {
        this.reimagingMarker = m;
      } else {
        this.dialogTitle = 'Re-capture';
        this.dialogMessage = "This scan has no street-view captures, so the image can't be re-captured here.";
        this.$refs.dialogMessage.open();
      }
    },
    cancelReimage() {
      this.reimagingMarker = null;
      this.$refs.modelViewer?.cancelBubbleReimage?.();
    },
    // Just after creating a marker/hotspot from the toolbar, drop straight into street view to capture
    // its thumbnail (the chosen create flow). Silent when the scan has no street-view captures — the
    // user can Add image later from the panel.
    async enterCaptureForNew(created) {
      if (!created?.id) return;
      await this.$nextTick();
      // Prefer the stored instance (correct domain type for the thumbnail routing); fall back to the
      // freshly-created object if the store hasn't flushed yet.
      const target = this.markers.find(x => x.id === created.id) || created;
      const entered = await this.$refs.modelViewer?.startBubbleReimage?.(target);
      if (process.env.NODE_ENV === 'development' && !entered) {
        // eslint-disable-next-line no-console
        console.warn('[capture-new] did not enter street view', { id: created.id, scanId: target?.scanId, hasTransform: !!target?.transform });
      }
      if (entered) this.reimagingMarker = target;
    },
    // Result of a re-capture draw. IMAGE ONLY: refresh the thumbnail and (re)write the provenance
    // record. The marker's 3D position, transform, status and review are deliberately NOT touched —
    // no marker update, no model save.
    async onBubbleReimage(data) {
      const { marker, imageDataUrl, provenance } = data || {};
      this.reimagingMarker = null;
      this.$refs.modelViewer?.exitBubbleForAction?.();
      if (!marker?.id) return;

      // No resolvable source frame → don't commit a thumbnail that would disagree with the (absent) box.
      if (!provenance) {
        this.openSaveDialog('error', "Couldn't find a source frame for that box — the image wasn't "
          + 'changed. Draw over a spot that a capture photographed.');
        return;
      }

      // Show the loader the instant street view exits, then do the work. Replace the provenance first;
      // only swap the thumbnail if that succeeds, so the image and the record stay in lock-step.
      // Hotspots and markers share the provenance write (keyed by id); their thumbnails live in
      // different stores.
      this.openSaveDialog('saving');
      const isHotSpot = marker instanceof HotSpotMarker;
      const provOk = await this.replaceMarkerProvenance(marker, provenance);
      const imgOk = provOk && imageDataUrl
        ? (isHotSpot ? await this.uploadHotSpotThumbnail(marker, imageDataUrl)
                     : await this.uploadAnnotationThumbnail(marker, imageDataUrl))
        : false;

      await this.$nextTick();
      this.$refs.modelViewer?.locateMarkerById?.(marker.id);

      if (provOk && imgOk) {
        this.openSaveDialog('done', 'Thumbnail updated');
      } else {
        this.openSaveDialog('error', "Saving the image didn't fully complete (permissions or network). "
          + 'Please draw it again — re-capturing replaces the record, so a retry fixes it.');
      }
    },
    // Thumbnail-save dialog: 'saving' shows a loader, 'done' auto-dismisses after a beat, 'error' waits
    // for the user to close it.
    openSaveDialog(phase, message = '') {
      if (this._saveDialogTimer) { clearTimeout(this._saveDialogTimer); this._saveDialogTimer = null; }
      this.saveDialog = { open: true, phase, message };
      if (phase === 'done') {
        this._saveDialogTimer = setTimeout(() => { this.saveDialog.open = false; }, 1500);
      }
    },
    async uploadAnnotationThumbnail(marker, dataUrl) {
      try {
        const blob = await (await fetch(dataUrl)).blob();
        const file = new File([blob], 'anomaly-area.webp', { type: 'image/webp' });
        const { base64String, fileExtension } = await getBase64AndExtension(file);
        const request = new UpdateMarkerThumbnailModel(
          this.selectedAsset.organizationId, marker.assetId, marker.id,
          base64String, fileExtension, marker.scanId);
        const updated = await MarkerService.UploadThumbnail(request);
        this.markerThumbnailUpdated?.(updated);
        return true;
      } catch {
        /* non-fatal for the create flow — the marker exists; the thumbnail just isn't attached */
        return false;
      }
    },
    // Re-capture thumbnail for a HOTSPOT (its own store), then refresh the open panel in place.
    async uploadHotSpotThumbnail(hotSpot, dataUrl) {
      try {
        const blob = await (await fetch(dataUrl)).blob();
        const file = new File([blob], 'hotspot.webp', { type: 'image/webp' });
        const { base64String, fileExtension } = await getBase64AndExtension(file);
        const request = new UpdateMarkerThumbnailModel(
          this.selectedAsset.organizationId, hotSpot.assetId, hotSpot.id,
          base64String, fileExtension, hotSpot.scanId);
        const updated = await HotSpotService.updateHotSpotThumbnail(request);
        if (updated) {
          this.updateMarkerInArray(updated);
          if (this.selectedHotSpot?.id === hotSpot.id && updated.thumbnailPath) {
            this.selectedHotSpot.thumbnailPath = updated.thumbnailPath;
          }
          this.hotSpotImageRefresh++;
        }
        return true;
      } catch {
        return false;
      }
    },
    createMarkerModelRequest(markerType) {
      // The category dropdown hands us the full MarkerType object. Store both its label (for
      // display/legacy resolution) and its id (markerTypeId — the stable category link used for
      // reporting and style resolution).
      const label = typeof markerType === 'string' ? markerType : markerType?.label;
      const markerTypeId = typeof markerType === 'string' ? null : markerType?.id;
      const point = this.selectedPointLocal ?? this.selectedPoint;
      const scanId = this.selectedScanId ?? this.modelVersion.associatedScan;
      return new CreateMarkerModel(this.selectedAsset.id, label, false, point.x, point.y, point.z, this.selectedPointInspectionAreaId, scanId, markerTypeId);
    },
    async onCreateInspectionAreaRequested(areaName) {
      this.$refs.modelViewer?.exitBubbleForAction?.();
      // Check if the point is inside an existing area. if so then cancel create and display error.
      if (this.selectedPointInspectionAreaId != null) {
        this.dialogTitle = "Invalid Point Selected";
        this.dialogMessage = "Select a point outside of an existing inspection area to continue.";
        this.$refs.dialogMessage.open();
        return;
      }

      const point = this.selectedPointLocal ?? this.selectedPoint;
      const scanId = this.selectedScanId ?? this.modelVersion.associatedScan;
      const request = new CreateInspectionAreaModel(this.selectedAsset.id, areaName, point.x, point.y, point.z, scanId);

      // check that the box doesn't conflict (within the same scan)
      const hasConflict = markerGeometry.inspectionAreaHasConflicts(request, this.markers, scanId);
      if (hasConflict) {
        this.dialogTitle = "New Zone Conflict";
        this.dialogMessage = "The boundaries of the new zone are too close to another zone. Select a point further away and try again.";
        this.$refs.dialogMessage.open();
        return;
      }

      const marker = await InspectionAreaService.createInspectionAreaAsync(request);

      // finds any hotspots or anomalies inside the area
      let markerIds = [];
      let hotSpotIds = [];
      ({ markerIds, hotSpotIds } = markerGeometry.findEncompassingMarkers(marker, this.markers, marker.scanId))

      marker.markerIds = markerIds;
      marker.hotSpotIds = hotSpotIds;

      // Persist the encompassed marker/hotspot associations via the plain update DTO — never the
      // live InspectionAreaMarker, whose THREE meshGroup can't be JSON-serialized (see the create
      // model's toJSON note). This mirrors the transform-save and rename paths.
      const update = this.buildInspectionAreaMarkerUpdate(marker, markerIds, hotSpotIds);
      const updatedMarket = await InspectionAreaService.updateInspectionArea(update);
      this.addMarkerToArray(updatedMarket);

      // Reveal the zones layer so the area the user just created is immediately visible — zones are
      // hidden by default. Setting the store flag drives ModelViewer's $subscribe →
      // applyLayerVisibility, which shows every zone mesh (the new one included).
      this.viewerUi.layers.zones = true;

      await this.updateMarkersWithNewInspectionAreaId(updatedMarket.scanId, markerIds, hotSpotIds);
    },
    async updateMarkersWithNewInspectionAreaId(scanId, markerIds, hotSpotIds){
      const markerUpdates = markerIds.map(id =>
            MarkerService.GetByIdAsync(scanId, id)
                .then(marker => this.updateMarkerInArray(marker))
        );

        const hotSpotUpdates = hotSpotIds.map(id =>
            HotSpotService.getHotSpotByIdAsync(scanId, id)
                .then(hotSpot => this.updateMarkerInArray(hotSpot))
        );

        await Promise.all([...markerUpdates, ...hotSpotUpdates]);
    },
    onTransformMarker(marker) {
      this.markerBeingTransformed = marker;

      if (marker instanceof InspectionAreaMarker) {
        if (this.viewerUi.layers.zones) {
          this.isInspectionAreaOverlayVisible = false;
          this.$refs.modelViewer.transformMarker(marker);
        } else {
          this.dialogTitle = "Inspection Zone Limits Not Visible";
          this.dialogMessage = "Toggle the Inspection Zone marker visibility before continuing.";
          this.$refs.dialogMessage.open();
        }
      } else {
        this.$refs.modelViewer.transformMarker(marker);
      }
    },
    onTransformCancelled() {
      this.isTransformActive = false;
      this.isTransformSaving = false;
      this.$refs.modelViewer.cancelTransformMarker();

      if (this.markerBeingTransformed instanceof InspectionAreaMarker) {
        this.isInspectionAreaOverlayVisible = true;
      }

      this.markerBeingTransformed = null;
    },
    async onTransformSaveClicked() {
      this.isTransformSaving = true;

      // saveMarkerTransform returns false when it bails out early (e.g. an inspection-area
      // conflict) without emitting markerModified — in that case nothing else will clear the
      // spinner, so reset it here. On a real save it emits markerModified and onMarkerModified
      // owns the spinner lifecycle (incl. resetting it if the API call fails).
      const willModify = await this.$refs.modelViewer.saveMarkerTransform();
      if (!willModify) {
        this.isTransformSaving = false;
      }
    },
    displayConflictMessage() {
      this.dialogTitle = "Conflict between 2 Inspection Areas";
      this.dialogMessage = "When modifying an inspection area make sure that it doesn't intersect with an existing area.";
      this.$refs.dialogMessage.open();
    },
    displayAreaConflictDialog() {
      this.dialogTitle = "Cannot Update Area";
      this.dialogMessage = "Ensure that the inspection zone does not intersect with another zone.";
      this.$refs.dialogMessage.open();
    },
    async onMarkerModified(modifiedMarker, markerIds, hotspotIds) {
      try {
        let saved;
        if (modifiedMarker instanceof InspectionAreaMarker) {
          const updateModel = this.buildInspectionAreaMarkerUpdate(modifiedMarker, markerIds, hotspotIds);
          saved = await this.updateInspectionZone(updateModel);
        } else if (modifiedMarker instanceof HotSpotMarker) {
          saved = await HotSpotService.updateHotSpotMarker(modifiedMarker);
        } else {
          saved = await MarkerService.UpdateMarker(modifiedMarker);
        }

        // Refresh the live marker's ETag from the server's response so a follow-up edit of the
        // same marker doesn't send a stale If-Match and 412. modifiedMarker is the in-scene
        // object (carries meshGroup), so we mutate its eTag in place rather than swap the object.
        if (saved?.eTag) {
          modifiedMarker.eTag = saved.eTag;
        }

        // Moving/resizing a zone changes which anomalies + hotspots it encompasses, so the server
        // returns recomputed counts/integrity. Fold them back into the live marker, re-render its
        // on-box label card, and sync any open detail panels (which hold fetched DTOs, not the live
        // marker) so every surface reflects the new record.
        if (modifiedMarker instanceof InspectionAreaMarker) {
          this.syncInspectionAreaRecord(modifiedMarker, saved);
          modifiedMarker.refreshLabel?.();
          this.refreshInspectionAreaPanels(modifiedMarker, saved);
        }

        this.updateMarkerInArray(modifiedMarker);
      } catch (error) {
        // Tell the user why the save failed; the transform UI is torn down in finally either way.
        if (error?.status === 412) {
          this.dialogTitle = "Update Conflict";
          this.dialogMessage = "This item was changed by someone else since you opened it. Reload to get the latest version, then re-apply your change.";
        } else {
          this.dialogTitle = "Could Not Save Changes";
          this.dialogMessage = error?.message ?? "An unexpected error occurred while saving. Please try again.";
        }
        this.$refs.dialogMessage.open();
      } finally {
        // Exit transform mode whether the save succeeded or threw: restore the right panel,
        // hide the EditMarkerToolBar, and clear the spinner.
        if (modifiedMarker instanceof InspectionAreaMarker) {
          this.isInspectionAreaOverlayVisible = true;
        }

        this.isTransformActive = false;
        this.markerBeingTransformed = null;
        this.isTransformSaving = false;
      }
    },

    async updateInspectionZone(updatedMarker) {
      const anomalyIds = [
        ...new Set(
          this.markers
            .filter(m =>
              (m.inspectionAreaId == updatedMarker.id) &&
              m instanceof AnomalyMarker
            )
            .map(m => m.id)
            .concat(updatedMarker.markerIds)
        )
      ];

      const hotSpotIds = [
        ...new Set(
          this.markers
            .filter(m =>
              (m.inspectionAreaId == updatedMarker.id) &&
              m instanceof HotSpotMarker
            )
            .map(m => m.id)
            .concat(updatedMarker.hotSpotIds)
        )
      ];

      const updatedArea = await InspectionAreaService.updateInspectionArea(updatedMarker);
      await this.replaceZoneAssociatedMarkers(updatedMarker.scanId, anomalyIds);
      await this.replaceZoneAssociatedHotSpots(updatedMarker.scanId, hotSpotIds);

      // Hand the fresh record (with the new ETag) back to the caller so it can refresh
      // the in-scene zone marker and avoid a stale-ETag 412 on the next edit.
      return updatedArea;
    },
    async replaceZoneAssociatedMarkers(scanId, markerIds = []) {
      for (const id of markerIds) {
        const updatedMarker = await MarkerService.GetByIdAsync(scanId, id);
        this.updateMarkerInArray(updatedMarker);
      }
    },
    async replaceZoneAssociatedHotSpots(scanId, hotSpotIds = []) {
      for (const id of hotSpotIds) {
        const updatedMarker = await HotSpotService.getHotSpotByIdAsync(scanId, id);
        this.updateMarkerInArray(updatedMarker);
      }
    },
    buildInspectionAreaMarkerUpdate(modifiedMarker, markerIds, hotSpotIds) {
      return new UpdateInspectionAreaModel(modifiedMarker.id, modifiedMarker.assetId, modifiedMarker.transform, markerIds, hotSpotIds, modifiedMarker.name, modifiedMarker.eTag, modifiedMarker.scanId);
    },
    // Copy the server's recomputed zone metrics (marker/hotspot counts, integrity, ETag) from a
    // saved record onto any object that mirrors that zone (the live marker or a panel DTO).
    syncInspectionAreaRecord(target, saved) {
      if (!target || !saved) return;
      target.numberOfMarkers = saved.numberOfMarkers;
      target.numberOfHotspots = saved.numberOfHotspots;
      target.integrityScore = saved.integrityScore;
      target.integrityScorePercentage = saved.integrityScorePercentage;
      if (saved.eTag) target.eTag = saved.eTag;
    },
    // Keep the open zone detail surfaces in step with a transform. Both panels hold fetched DTOs
    // (not the live marker), so mirror the fresh metrics onto them and re-derive their member lists
    // from the array — replaceZoneAssociated* has already updated each anomaly/hotspot's zone id.
    refreshInspectionAreaPanels(modifiedMarker, saved) {
      if (this.inspectionAreaBeingEdited?.id === modifiedMarker.id && this.inspectionAreaBeingEdited !== modifiedMarker) {
        this.syncInspectionAreaRecord(this.inspectionAreaBeingEdited, saved);
      }
      if (this.navigatedInspectionArea?.id === modifiedMarker.id) {
        this.syncInspectionAreaRecord(this.navigatedInspectionArea, saved);
        this.navigatedInspectionArea.markers = this.markers.filter(
          (m) => m instanceof AnomalyMarker && m.inspectionAreaId == modifiedMarker.id,
        );
        this.navigatedInspectionArea.hotSpotMarkers = this.markers.filter(
          (m) => m instanceof HotSpotMarker && m.inspectionAreaId == modifiedMarker.id,
        );
      }
    },
    async markerReviewStatusChanged(updatedMarker){
      this.updateMarkerInArray(updatedMarker);
    },
    async onNavigatedToInspectionArea(inspectionArea) {
      let area = inspectionArea;
      if (inspectionArea != null) {
        const anomalyMarkers = this.markers.filter(mrkr => mrkr instanceof AnomalyMarker && mrkr.inspectionAreaId == area.id);
        const hotSpotMarkers = this.markers.filter(mrkr => mrkr instanceof HotSpotMarker && mrkr.inspectionAreaId == area.id);
        area = await InspectionAreaService.getInspectionZoneByScanId(inspectionArea.scanId, inspectionArea.id);
        area.markers = anomalyMarkers;
        area.hotSpotMarkers = hotSpotMarkers;
      }
      this.navigatedInspectionArea = area;
    },
    onToggleZoneIsolation(enabled) {
      this.$refs.modelViewer?.setZoneIsolation(enabled);
    },
    onNavigateToMarker(marker) {
      this.$refs.modelViewer.locateMarkerById(marker.id);
    },
    navigateToHotSpot(hotSpot) {
      this.selectedHotSpot = hotSpot;
      this.$refs.modelViewer.navigateToHotSpot(hotSpot.id);
    },
    onDisplayInspectionAreaDetails(inspectionArea) {

      this.inspectionAreaBeingEdited = inspectionArea;
      this.isInspectionAreaOverlayVisible = true;
    },
    async onRenameInspectionArea(newName) {
      const area = this.inspectionAreaBeingEdited;
      const name = (newName ?? '').trim();
      if (!area || !name || name === area.name) return;

      try {
        // Rename only: pass null transform/marker/hotspot ids so the API leaves those untouched.
        const update = new UpdateInspectionAreaModel(area.id, area.assetId, null, null, null, name, area.eTag, area.scanId);
        const updated = await InspectionAreaService.updateInspectionArea(update);
        const newName = updated?.name ?? name;

        // Update the panel's object (the fetched DTO) so the dialog/JIT reflect the new name.
        area.name = newName;
        if (updated?.eTag) area.eTag = updated.eTag;

        // Update the live marker instance in the viewer (the one carrying the markRaw'd meshGroup).
        // updateName rebuilds the 3D name label; mutating area.name alone wouldn't touch the sprite.
        const markerInstance = this.markers.find(
          (m) => m.id === area.id && m instanceof InspectionAreaMarker
        );
        if (markerInstance) {
          markerInstance.updateName(newName);
          if (updated?.eTag) markerInstance.eTag = updated.eTag;
          this.updateMarkerInArray(markerInstance);
        }

        this.dialogTitle = "Inspection Area Renamed";
        this.dialogMessage = "The inspection area name was updated.";
        this.$refs.dialogMessage.open();
      } catch (e) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Failed to rename inspection area', e);
        }
        this.dialogTitle = "Rename Failed";
        this.dialogMessage = "Could not rename the inspection area. Please try again.";
        this.$refs.dialogMessage.open();
      }
    },
    async onDeleteInspectionArea(area) {
      const target = area ?? this.inspectionAreaBeingEdited;
      if (!target) return;

      try {
        // Success = no thrown ApiError (a 204 resolves to null), so don't gate on the return value.
        await InspectionAreaService.deleteInspectionZoneAsync(target);

        this.removeMarkerFromArray(target);  // store.remove → the zone's mesh is removed on re-sync
        this.isInspectionAreaOverlayVisible = false;
        this.inspectionAreaBeingEdited = null;

        this.dialogTitle = "Inspection Area Deleted";
        this.dialogMessage = "The inspection area was removed.";
        this.$refs.dialogMessage.open();
      } catch (e) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Failed to delete inspection area', e);
        }
        this.dialogTitle = "Delete Failed";
        this.dialogMessage = "Could not delete the inspection area. Please try again.";
        this.$refs.dialogMessage.open();
      }
    },

    // Success feedback is handled by the saving dialog (see onBubbleReimage); these just sync state so
    // the open detail panel re-signs its thumbnail.
    async markerThumbnailUpdated(updated) {
      if (updated) {
        this.updateMarkerInArray(updated);
        this.markerImageRefresh++;   // re-sign the thumbnail in the open detail panel (same marker id)
      }
    },

    async hotSpotThumbnailUpdated(hotSpot) {
      if (hotSpot) {
        this.updateMarkerInArray(hotSpot);
      }
    },

    async hotSpotDeleted(hotSpot) {
      this.selectedHotSpot = null;
      this.selectedMarker = null;   // side panel closes → back to the markers list

      if (this.navigatedInspectionArea != null) {
        const updatedZone = await InspectionAreaService.getInspectionZoneByScanId(this.navigatedInspectionArea.scanId, this.navigatedInspectionArea.id);
        this.onNavigatedToInspectionArea(updatedZone);
      }

      this.removeMarkerFromArray(hotSpot);

      this.dialogTitle = "HotSpot Deleted";
      this.dialogMessage = "HotSpot was successfully deleted.";
      this.$refs.dialogMessage.open();
    },

    updateHotSpotImageUri(hotSpot) {
      this.markerStore.update(hotSpot);
    },

    async createHotSpot() {
      this.$refs.modelViewer?.exitBubbleForAction?.();
      const point = this.selectedPointLocal ?? this.selectedPoint;
      const scanId = this.selectedScanId ?? this.modelVersion.associatedScan;
      const inspectionZoneId = markerGeometry.findInspectionAreaForPoint(point, this.markers, scanId);
      const hotSpot = new CreateHotSpotModel(this.selectedAsset.id, point.x, point.y, point.z, [], inspectionZoneId, scanId);
      const anomalyIds = markerGeometry.findMarkersInsideHotSpot(hotSpot, this.markers, scanId);
      hotSpot.anomalyIds = anomalyIds;

      const newHotSpot = await HotSpotService.createHotSpot(hotSpot);
      if (newHotSpot != null) {
        this.addMarkerToArray(newHotSpot);

        if (hotSpot.inspectionAreaId != null && hotSpot.inspectionAreaId != "") {
          const updatedZone = await InspectionAreaService.getInspectionZoneByScanId(hotSpot.scanId, hotSpot.inspectionAreaId);
          this.onNavigatedToInspectionArea(updatedZone);
        }

        // Drop into street view to capture the new hotspot's thumbnail.
        await this.enterCaptureForNew(newHotSpot);
      }
    },

    // Below this line it hasn't been reviewed.




    onTransformActiveChanged(isVisible) {
      this.isTransformActive = isVisible;
    },
    onScaleTransformSelected() {
      this.$refs.modelViewer.setTransformToScale();
    },
    onRotateTransformSelected() {
      this.$refs.modelViewer.setTransformToRotate();
    },
    onMoveTransformSelected() {
      this.$refs.modelViewer.setTransformToMove();
    },

    onPointSelected(data) {
      this.pendingAnnotationImage = null;   // a normal point pick is not a street-view annotation
      this.selectedPoint = data.point;
      this.selectedPointLocal = data.localPoint ?? data.point;
      this.selectedScanId = data.scanId ?? this.modelVersion.associatedScan;
      this.selectedPointInspectionAreaId = data.inspectionAreaId;

      // Same scan-scoped filter as onMarkerSelected — when placing a marker against a specific
      // scan we want the images from that scan's cameras, not collisions from other scans.
      const filenameSet = new Set(data.imageFilenames);
      const clickedScanId = this.selectedScanId;
      this.identifiedFrames = this.frames.filter(frame =>
        filenameSet.has(frame.fileName) && (!clickedScanId || frame.scanId === clickedScanId),
      );
    },

    deselectAnomaly() {
      this.selectedMarker = null;
    },

    onPointDeselected() {
      this.selectedPoint = null;
      this.selectedPointLocal = null;
      this.selectedScanId = null;
      this.selectedPointInspectionAreaId = null;
      this.pendingAnnotationImage = null;
    }
  }
};
</script>

<style scoped>
/* Fill the whole content area beside the app sidebar, full height. Negative margins cancel the
   .scrollable-body padding so the viewer is edge-to-edge; v-main already offsets for the sidebar
   (desktop) and the top app bar (mobile), so no fixed positioning / manual offset is needed. */
.viewer-container {
  position: relative;
  margin: -20px;
  width: auto;
  height: calc(100% + 40px);
  min-height: 0;
  padding: 0;
  max-width: none;
  background-color: #1a1f24;
  overflow: hidden;
}

.mobile-back-bar {
  padding: 0 4px 12px;
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.viewer-stage {
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 0;
  /* Shared width for the full-height right Markers panel; the top toolbar stops just left of it. */
  --panel-w: clamp(300px, 24vw, 360px);
}

.full-size-viewer {
  width: 100%;
  height: 100%;
}

.viewer-wrap {
  position: absolute;
  inset: 0;
  display: flex;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  overflow: hidden;
  box-shadow: 0 4px 18px rgba(0, 0, 0, 0.35);
}

.viewer-wrap > :deep(*) {
  flex: 1 1 auto;
  min-height: 0;
  width: 100%;
}

/* ===== Desktop viewer chrome (two floating pills + side panels) ===== */
/* Shared pill look for the breadcrumb + tools bars. */
.viewer-crumb, .viewer-tools {
  position: absolute;
  /* Clear a top notch / status bar on notched landscape tablets. */
  top: calc(12px + env(safe-area-inset-top, 0px));
  z-index: 20;
  height: clamp(46px, calc(34px + 0.9vw), 52px);
  align-items: center;
  background: rgba(20, 30, 36, 0.82);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.35);
  backdrop-filter: blur(6px);
}

/* Breadcrumb pill — top-left, hugs its content. */
.viewer-crumb {
  left: calc(12px + env(safe-area-inset-left, 0px));
  max-width: calc(100% - var(--panel-w) - 36px);
  gap: 8px;
  padding: 0 14px 0 8px;
}

/* Tools pill — floats to the right, just left of the Markers panel. */
.viewer-tools {
  right: calc(var(--panel-w) + 24px + env(safe-area-inset-right, 0px));
  gap: 6px;
  padding: 0 10px;
}

.tb-back {
  flex: 0 0 auto; display: inline-flex; align-items: center; justify-content: center;
  width: 34px; height: 34px; border-radius: 9px;
  background: rgba(255, 255, 255, 0.05); border: 1px solid rgba(255, 255, 255, 0.08);
  color: var(--duallo-accent); cursor: pointer; transition: background 0.15s ease, border-color 0.15s ease;
}
.tb-back:hover { background: rgba(100, 255, 218, 0.12); border-color: rgba(100, 255, 218, 0.35); }

.tb-crumbs { display: flex; align-items: center; gap: 6px; min-width: 0; }
.tb-crumb { background: none; border: none; padding: 0; font: inherit; font-size: 0.86rem; color: #8aa0aa; cursor: pointer; white-space: nowrap; }
.tb-crumb:hover:not(.current) { color: #cfd8dc; }
.tb-crumb.current { color: #fff; font-weight: 700; overflow: hidden; text-overflow: ellipsis; }
.tb-sep { color: #5c6f78; }
.tb-version { color: #9db0ba; font-size: 0.82rem; white-space: nowrap; }

.tb-mode {
  display: inline-flex; align-items: center; gap: 7px;
  height: clamp(32px, calc(24px + 0.6vw), 36px); padding: 0 14px; border-radius: 9px;
  background: rgba(255, 255, 255, 0.05); border: 1px solid rgba(255, 255, 255, 0.08);
  color: #e3eef0; font: inherit; font-size: 0.85rem; font-weight: 600; cursor: pointer; white-space: nowrap;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}
.tb-mode:hover { background: rgba(255, 255, 255, 0.09); }
.tb-mode.active { background: rgba(100, 255, 218, 0.14); border-color: rgba(100, 255, 218, 0.4); color: var(--duallo-accent); }
.tb-mode.is-disabled, .tb-mode:disabled { opacity: 0.4; cursor: not-allowed; }
.tb-mode.peg-btn:not(.is-disabled) { cursor: grab; touch-action: none; }
.tb-mode.peg-btn:not(.is-disabled):active { cursor: grabbing; }

.tb-div { width: 1px; height: 26px; background: rgba(255, 255, 255, 0.12); margin: 0 4px; }

.tb-icon {
  display: inline-flex; align-items: center; justify-content: center;
  width: clamp(32px, calc(24px + 0.6vw), 36px); height: clamp(32px, calc(24px + 0.6vw), 36px); border-radius: 9px;
  background: transparent; border: 1px solid transparent; color: #8aa0aa; cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}
.tb-icon:hover { background: rgba(255, 255, 255, 0.06); color: #e3eef0; }
.tb-icon.active { background: rgba(100, 255, 218, 0.14); border-color: rgba(100, 255, 218, 0.4); color: var(--duallo-accent); }

/* Floating side panels: sit below the toolbar, over the canvas. */
.viewer-right {
  position: absolute; top: 12px; right: 12px; bottom: 12px; z-index: 15;
  width: var(--panel-w);
  display: flex;
}
/* Active tool's panel: drops directly under the tools pill (right side), not on the left. */
.viewer-tool-panel {
  position: absolute; top: 76px; right: calc(var(--panel-w) + 24px); bottom: 12px; z-index: 15;
  width: clamp(280px, 22vw, 340px);
  display: flex;
}
.viewer-right .markers-panel { width: 100%; }
.viewer-tool-panel .rail-panel { width: 100%; max-height: 100%; }

/* ═══════════ Mobile viewer chrome ═══════════ */
.mv-topbar {
  position: absolute; top: calc(12px + env(safe-area-inset-top, 0px)); left: 12px; right: 12px; z-index: 20;
  display: flex; align-items: center; gap: 8px;
}
.mv-round {
  flex: 0 0 auto; width: 44px; height: 44px; border-radius: 22px;
  border: 1px solid rgba(255, 255, 255, 0.12); background: rgba(18, 25, 30, 0.85); color: #eef3f2;
  display: flex; align-items: center; justify-content: center; cursor: pointer; backdrop-filter: blur(6px);
}
.mv-title {
  flex: 1 1 auto; min-width: 0; height: 44px; padding: 0 14px; border-radius: 22px;
  border: 1px solid rgba(255, 255, 255, 0.1); background: rgba(18, 25, 30, 0.85);
  display: flex; flex-direction: column; justify-content: center; backdrop-filter: blur(6px);
}
.mv-title-main { font-size: 0.9rem; font-weight: 700; color: #fff; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.mv-title-sub { font-size: 0.72rem; color: #8aa0aa; }

/* Bottom-right, within thumb reach, sitting just above the markers peek sheet (which is 112px tall,
   so this clears it with a small gap). */
.mv-rail {
  position: absolute; bottom: calc(124px + env(safe-area-inset-bottom, 0px)); right: 12px; z-index: 19;
  display: flex; flex-direction: column; border-radius: 26px;
  border: 1px solid rgba(255, 255, 255, 0.1); background: rgba(18, 25, 30, 0.85); overflow: hidden; backdrop-filter: blur(6px);
}
.mv-rail-btn { width: 52px; height: 52px; border: none; background: transparent; color: #eef3f2; display: flex; align-items: center; justify-content: center; cursor: pointer; }
.mv-rail-btn + .mv-rail-btn { border-top: 1px solid rgba(255, 255, 255, 0.08); }
.mv-rail-btn.active { color: var(--duallo-accent); background: rgba(100, 255, 218, 0.1); }
.mv-rail-btn.is-disabled { opacity: 0.4; cursor: not-allowed; }

/* Layers panel opens upward, above the rail. */
.mv-layers { position: absolute; bottom: calc(234px + env(safe-area-inset-bottom, 0px)); right: 12px; z-index: 21; width: min(260px, calc(100% - 24px)); }
/* Walkthrough playback drop-panel — same anchor as the layers panel (mutually exclusive). */
.mv-walk { position: absolute; bottom: calc(234px + env(safe-area-inset-bottom, 0px)); right: 12px; z-index: 21; width: min(300px, calc(100% - 24px)); }
.mv-drop-enter-active, .mv-drop-leave-active { transition: opacity 0.18s ease, transform 0.18s ease; transform-origin: bottom right; }
.mv-drop-enter-from, .mv-drop-leave-to { opacity: 0; transform: translateY(8px); }

.mv-sheet {
  position: absolute; left: 0; right: 0; bottom: 0; z-index: 18;
  max-height: 90dvh; box-sizing: border-box;
  padding: 8px 14px calc(18px + env(safe-area-inset-bottom, 0px));
  border-radius: 22px 22px 0 0; background: #1a2227; border-top: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 -10px 30px rgba(0, 0, 0, 0.4);
  display: flex; flex-direction: column; gap: 10px; transition: height 0.25s ease, max-height 0.25s ease;
}
.mv-handle { align-self: center; width: 80px; height: 16px; border: none; background: transparent; display: flex; align-items: center; justify-content: center; cursor: pointer; padding: 0; }
.mv-handle span { width: 40px; height: 4px; border-radius: 2px; background: #4a5a61; }
.mv-sheet-head { display: flex; align-items: baseline; gap: 8px; padding: 0 2px 4px; border: none; background: transparent; color: #eef3f2; cursor: pointer; text-align: left; }
.mv-sheet-title { font-size: 1.05rem; font-weight: 700; color: #fff; }
.mv-sheet-meta { font-size: 0.8rem; color: #8aa0aa; }
.mv-sheet-meta .amber { color: #f0a623; }
.mv-sheet-all { margin-left: auto; font-size: 0.82rem; font-weight: 600; color: var(--duallo-accent); }
.mv-list, .mv-detail { flex: 1 1 auto; min-height: 0; }
/* Flatten the reused list/detail cards inside the sheet (the sheet is the surface). */
.mv-sheet :deep(.markers-card), .mv-sheet :deep(.mdp), .mv-sheet :deep(.sidebar-card) { background: transparent; border: none; box-shadow: none; border-radius: 0; }
/* Comfortable sizing on mobile — target the LIST rows only (grid keeps its own aspect-ratio thumb). */
.mv-sheet :deep(.marker-row.is-list) { padding: 10px 8px; gap: 12px; }
.mv-sheet :deep(.marker-row.is-list .marker-thumb) { flex: 0 0 auto; width: 56px; height: 44px; }
.mv-sheet :deep(.marker-label) { font-size: 0.95rem; }
.mv-sheet :deep(.marker-sub) { font-size: 0.8rem; }
.mv-sheet :deep(.results-list.view-grid) { grid-template-columns: repeat(auto-fill, minmax(150px, 1fr)); gap: 10px; }
/* Explicit thumb height — aspect-ratio doesn't resolve reliably inside the sheet's grid tracks. */
.mv-sheet :deep(.marker-row.is-grid .marker-thumb) { width: 100%; height: 104px; aspect-ratio: auto; }
/* Match the asset list: breathing room between items and the scrollbar. */
.mv-sheet :deep(.results-list) { padding: 10px 10px 12px 4px; }

/* Bubble (street view) exit — a pill under the tools, same look as the tools bar. */
.viewer-bubble-exit {
  position: absolute; bottom: 16px; left: 50%; transform: translateX(-50%); z-index: 16;
  align-items: center; gap: 10px; max-width: calc(100% - 24px);
  height: clamp(46px, calc(34px + 0.9vw), 52px); padding: 0 10px 0 14px;
  background: rgba(20, 30, 36, 0.82); border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 12px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.35); backdrop-filter: blur(6px);
  color: #e3eef0; font-size: 0.85rem; font-weight: 600;
}
.be-label { display: inline-flex; align-items: center; gap: 6px; white-space: nowrap; }
.be-sep { width: 1px; align-self: stretch; margin: 8px 2px; background: rgba(255, 255, 255, 0.12); }
.be-steps { display: inline-flex; align-items: center; gap: 4px; }
.be-counter { display: inline-flex; align-items: center; gap: 2px; min-width: 66px; justify-content: center; font-variant-numeric: tabular-nums; font-size: 0.84rem; }
.be-jump-list { max-height: 280px; overflow-y: auto; background: #10171d; }
.be-hint { font-size: 0.78rem; color: #9fb2bb; white-space: nowrap; }
.be-btn {
  flex: 0 0 auto; display: inline-flex; align-items: center; gap: 6px;
  height: clamp(32px, calc(24px + 0.6vw), 36px); padding: 0 14px; border-radius: 9px;
  background: rgba(255, 255, 255, 0.05); border: 1px solid rgba(255, 255, 255, 0.08); color: #e3eef0;
  font: inherit; font-size: 0.85rem; font-weight: 600; cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}
.be-btn:hover:not(:disabled) { background: rgba(255, 255, 255, 0.09); color: #fff; }
.be-btn:disabled { opacity: 0.45; cursor: not-allowed; }
.be-step { padding: 0 8px; }
.be-mark { color: var(--duallo-accent); border-color: rgba(100, 255, 218, 0.3); }
.be-create { color: var(--duallo-accent); border-color: rgba(100, 255, 218, 0.35); }
.be-create:hover:not(:disabled) { background: rgba(100, 255, 218, 0.12); }
@media (max-width: 1440px) { .be-text { display: none; } }

/* Thumbnail save dialog (loading → success/error). */
.save-modal {
  display: flex; flex-direction: column; align-items: center; gap: 10px;
  background: var(--duallo-card-gradient); border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 16px; box-shadow: 0 24px 60px rgba(0, 0, 0, 0.45);
  color: #fff; padding: 26px 24px 22px; text-align: center;
}
.save-icon {
  width: 54px; height: 54px; display: flex; align-items: center; justify-content: center;
  border-radius: 14px; background: rgba(100, 255, 218, 0.08); border: 1px solid rgba(100, 255, 218, 0.22);
}
.save-icon.done { background: rgba(74, 222, 128, 0.1); border-color: rgba(74, 222, 128, 0.28); }
.save-icon.error { background: rgba(239, 68, 68, 0.1); border-color: rgba(239, 68, 68, 0.25); }
.save-title { font-size: 1.1rem; font-weight: 700; margin: 2px 0 0; }
.save-text { font-size: 0.9rem; color: #b0bec5; margin: 0; line-height: 1.45; }
.save-bar { width: 180px; margin-top: 4px; }
.save-close {
  margin-top: 8px; padding: 9px 20px; border-radius: 10px;
  background: rgba(255, 255, 255, 0.05); border: 1px solid rgba(255, 255, 255, 0.14); color: #e6edf0;
  font: inherit; font-weight: 600; cursor: pointer; transition: background 0.15s ease;
}
.save-close:hover { background: rgba(255, 255, 255, 0.1); }

/* Narrower desktop / higher zoom (e.g. 150% on a 1080p screen ≈ 1280 CSS px): trim the version
   label, then drop the Street view / Walkthrough text to icon-only so the two pills never collide. */
@media (max-width: 1500px) {
  .tb-version { display: none; }
}
@media (max-width: 1440px) {
  .tb-mode span { display: none; }
  .tb-mode { padding: 0 10px; gap: 0; }
}

/* ----- Compact header (desktop): a distinct Back button + the asset name as a label ----- */
.asset-crumb {
  position: absolute;
  top: 14px;
  left: 14px;
  z-index: 20;
  display: flex;
  align-items: center;
  gap: 8px;
  max-width: 340px;
  padding: 5px 14px 5px 5px;
  background: rgba(20, 30, 36, 0.82);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.35);
  backdrop-filter: blur(6px);
}

/* The back control reads as a real button (filled chip) so it's clearly separate from the name. */
.crumb-back {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 32px;
  padding: 0 12px 0 9px;
  border-radius: 9px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.08);
  color: #e3eef0;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;
}

.crumb-back:hover {
  background: rgba(100, 255, 218, 0.12);
  border-color: rgba(100, 255, 218, 0.35);
}

.crumb-back .v-icon { color: var(--duallo-accent); }

.crumb-divider {
  flex: 0 0 auto;
  width: 1px;
  height: 20px;
  background: rgba(255, 255, 255, 0.12);
}

.crumb-name {
  min-width: 0;
  font-size: 0.92rem;
  font-weight: 700;
  color: #ffffff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ----- Merged Create + Markers panel (left) ----- */
/* One card; the two inner .sidebar-cards (place-marker actions, then the filter/list) are flattened
   so they read as a single block separated by a hairline. */
.markers-panel {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  background: var(--duallo-card-gradient);
  border: 1px solid rgba(255, 255, 255, 0.04);
  border-radius: 12px;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.25);
  overflow: hidden;
}

.markers-panel :deep(.sidebar-card) {
  background: transparent;
  border: none;
  border-radius: 0;
  box-shadow: none;
}

/* Hairline between the create actions and the markers list. The place-marker toolbar keeps its
   content height (flex:0 0 auto) so the markers list can't squeeze/overlap it on shorter screens. */
.markers-panel > :deep(.sidebar-card:not(:last-child)) {
  flex: 0 0 auto;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

/* The markers list fills the remaining height; its own inner list scrolls. */
.markers-panel > :deep(.sidebar-card:last-child) {
  flex: 1 1 auto;
  min-height: 0;
}

/* ----- Baking banner (floats top-centre over the 3D viewer) ----- */
.baking-banner {
  position: absolute;
  top: 76px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 25;
  display: flex;
  align-items: center;
  gap: 10px;
  max-width: min(560px, calc(100% - 120px));
  padding: 10px 12px 10px 14px;
  background: linear-gradient(180deg, #2a2620 0%, #241f18 100%);
  border: 1px solid rgba(255, 209, 102, 0.45);
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5);
  color: #f5e9d0;
  pointer-events: auto;
}

.baking-banner-icon {
  flex: 0 0 auto;
}

.baking-banner-text {
  flex: 1 1 auto;
  font-size: 0.85rem;
  font-weight: 600;
  line-height: 1.35;
}

.baking-banner-close {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 8px;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: #cdbf9f;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}

.baking-banner-close:hover {
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
}

/* ----- Floating sidebars ----- */
/* Laid out as a row so the collapse toggle sits as a handle on the inner edge — out of the
   content's vertical flow — letting the panel content use the full available height. */
.floating-sidebar {
  position: absolute;
  top: 14px;
  bottom: 14px;
  width: 360px;
  display: flex;
  align-items: stretch;
  gap: 6px;
  z-index: 15;
  transition: width 0.25s ease;
  pointer-events: none;
}

.floating-sidebar > * {
  pointer-events: auto;
}

/* Collapsed = only the side handle remains */
.floating-sidebar.is-collapsed {
  width: 26px;
}

.sidebar-left {
  left: 14px;
  flex-direction: row;          /* content | handle (handle on the inner/right edge) */
}

.sidebar-right {
  right: 14px;
  flex-direction: row-reverse;  /* handle (inner/left edge) | content */
}

/* ----- Right tool rail ----- */
/* Shrinks to its content (panel + rail) instead of a fixed 360px column, so when no tool is open
   only the slim rail sits top-right and the canvas keeps the full width. */
.sidebar-right.viewer-rail-side {
  width: auto;
  align-items: flex-start;      /* rail hugs the top; doesn't stretch full height */
  flex-direction: row;          /* panel | rail (rail on the outer/right edge) */
  gap: 8px;
}

.rail-panel {
  width: 320px;
  max-height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-right: 2px;
}

.rail-panel::-webkit-scrollbar { width: 6px; }
.rail-panel::-webkit-scrollbar-thumb { background: #3a4a52; border-radius: 3px; }
.rail-panel::-webkit-scrollbar-thumb:hover { background: var(--duallo-accent-muted); }

.tool-rail {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 8px;
  background: var(--duallo-card-gradient);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 14px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.4);
}

.rail-btn {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 1px solid transparent;
  color: #8aa0aa;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.rail-btn:hover {
  background: rgba(255, 255, 255, 0.05);
  color: #e3eef0;
}

.rail-btn.active {
  background: rgba(100, 255, 218, 0.14);
  border-color: rgba(100, 255, 218, 0.4);
  color: var(--duallo-accent);
}

.rail-btn:disabled,
.rail-btn.is-disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

/* The Street-view control is a drag handle, so it reads as grabbable. It shows the Google-Maps-style
   pegman figure and gives it a little hop/sway on hover. */
.peg-btn:not(.is-disabled) {
  cursor: grab;
  touch-action: none;
}

.peg-btn:not(.is-disabled):hover {
  background: rgba(100, 255, 218, 0.14);
  border-color: rgba(100, 255, 218, 0.4);
}

.peg-btn:not(.is-disabled):active { cursor: grabbing; }

.peg-fig {
  display: block;
  filter: drop-shadow(0 2px 2px rgba(0, 0, 0, 0.45));
  transform-origin: bottom center;
  transition: transform 0.15s ease;
}

.peg-btn:not(.is-disabled):hover .peg-fig {
  animation: peg-hop 0.6s ease-in-out infinite;
}

.peg-btn.is-disabled .peg-fig { filter: grayscale(1) opacity(0.6); }

@keyframes peg-hop {
  0%, 100% { transform: translateY(0) rotate(0deg); }
  25% { transform: translateY(-3px) rotate(-6deg); }
  75% { transform: translateY(-3px) rotate(6deg); }
}

@media (prefers-reduced-motion: reduce) {
  .peg-btn:not(.is-disabled):hover .peg-fig { animation: none; }
}

.rail-divider {
  width: 26px;
  height: 1px;
  background: rgba(255, 255, 255, 0.08);
  margin: 2px 0;
}

/* Side handle: vertically centred so it costs zero vertical space */
.sidebar-toggle {
  flex: 0 0 32px;
  width: 32px;
  height: 72px;
  align-self: center;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(180deg, #20303a 0%, #1b2930 100%);
  border: 1px solid rgba(100, 255, 218, 0.22);
  border-radius: 10px;
  color: #e3eef0;
  cursor: pointer;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.5);
  transition: background 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;
}

.sidebar-toggle:hover {
  background: linear-gradient(180deg, #27373f 0%, #20303a 100%);
  border-color: rgba(100, 255, 218, 0.5);
}

/* When the panel is collapsed the handle is the only thing on screen — make it bigger and
   more obvious so it's an easy target to reopen the panel. */
.floating-sidebar.is-collapsed .sidebar-toggle {
  height: 92px;
  border-color: rgba(100, 255, 218, 0.45);
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.55);
}

.sidebar-content {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 4px;
  min-height: 0;
}

/* Left panel content clears the floating breadcrumb, leaving the same 14px gap as the outer margins. */
.sidebar-left .sidebar-content {
  padding-top: 58px;
}

/* The last card in each panel (left = Markers filter, right = Inspection Area) grows to
   fill the remaining height so the panel reaches the bottom; its inner list scrolls. */
.floating-sidebar .sidebar-content > *:last-child {
  flex: 1 1 auto;
  min-height: 0;
}

.sidebar-content > * {
  flex: 0 0 auto;
}

.sidebar-content::-webkit-scrollbar {
  width: 6px;
}

.sidebar-content::-webkit-scrollbar-thumb {
  background: #3a4a52;
  border-radius: 3px;
}

.sidebar-content::-webkit-scrollbar-thumb:hover {
  background: var(--duallo-accent-muted);
}

@media (max-width: 1263px) {
  .mobile-back-bar {
    padding: 10px 12px 8px;
  }
}

/* Street-view annotation type-picker dialog */
.bubble-type-card {
  border-radius: 14px !important;
  background: var(--duallo-card-gradient) !important;
  border: 1px solid rgba(255, 255, 255, 0.05);
  color: #fff;
}
.bubble-type-title { font-weight: 700; padding: 18px 20px 6px; }
.bt-primary {
  background: var(--duallo-accent-gradient) !important; color: #071514 !important;
  font-weight: 700 !important; text-transform: none !important; border-radius: 10px !important;
}
.bt-ghost {
  background: rgba(255, 255, 255, 0.04) !important; color: #cfd8dc !important;
  font-weight: 600 !important; text-transform: none !important; border-radius: 10px !important;
}
</style>
